import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  AlertCircle,
  ChevronDown,
  CloudDownload,
  Download,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type DetailContext,
  type FieldDef,
  type FilterDef,
  type FilterValues,
  type ResourceConfig,
  ResourcePage,
  csvName,
  fmtDate,
  isSet,
} from "@/components/resource-page";
import { BackArrow, backLinkClass, PageHeader } from "@/components/ui/page";
import { Badge } from "@/components/ui/badge";
import { Tabs } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Drawer, Modal } from "@/components/ui/dialog";
import { menuItemClass } from "@/components/ui/menu";
import { ClearButton, Field, Form, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { OptionSelect } from "@/components/ui/option-select";
import { DatePicker } from "@/components/ui/date-picker";
import { type CellKind, CsvEditor, type RowError, pageSlice } from "@/components/csv-editor";
import { api, errorMessage } from "@/services/api";
import { type Rank, atLeast, useCan, useRank } from "@/utils/permissions";
import { type ListParams, listAll } from "@/utils/geo";
import { cn } from "@/utils/utils";
import { columnKey, missingColumns, parseCsv, readCsvFile, toCsv } from "@/utils/csv";
import { downloadBlob, downloadText } from "@/utils/download";
import { StreamError, postEventStream } from "@/services/sse";
import type { ProblemDetail } from "@/meta/types";
import { birthDateMax, birthDateMin } from "@/utils/dates";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";
import { MemberDetail } from "@/pages/MemberDetailPage";

/** One remembered search, as the API returns it. */
interface SearchTerm {
  id: number;
  term: string;
  updated_at: string;
}

export interface Member {
  id: number;
  account_number: string;
  membership_type: string;
  prefix: string | null;
  suffix: string | null;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  snr_account_number: string | null;
  account_type: string;
  account_status: string;
  parent_account_number: string | null;
  supplementary_account_number: string | null;
  /** Counted by the API on the detail; a list row does not carry it. */
  has_supplementary_created: boolean;
  /** For a primary: every supplementary account under it, oldest first. */
  supplementary_account_numbers?: string[];
  /** Filled in when one member is read: the slots taken, and the configured ceiling. */
  supplementaries_in_use?: number;
  supplementary_limit?: number;
  family_member_account_number: string | null;
  employee_number: string | null;
  employee_relation: string | null;
  acquisition_type: string | null;
  membership_tier: string | null;
  /** Complimentary member: the `reasons` row who signed the membership off. */
  approved_by_id?: number | null;
  amendment: string | null;
  volume_shopper: boolean;
  co_brand_tagging: string | null;
  card_name: string | null;
  card_number: string | null;
  temporary_card_number: string | null;
  issued_date: string | null;
  valid_until: string | null;
  no_expiry: boolean;
  is_print_ready: boolean;
  union_bank_tag: string | null;
  is_temporarily_active: boolean;
  extended_time_minutes: number | null;
  signup_club_id: number | null;
  signup_club_name: string | null;
  active_club_id: number | null;
  active_club_name: string | null;
  renew_club_id: number | null;
  renew_club_name: string | null;
  city_id: number | null;
  city_name: string | null;
  barangay_id: number | null;
  barangay_name: string | null;
  address_street: string | null;
  unit_number: string | null;
  building_name: string | null;
  mobile_number: string | null;
  email: string | null;
  phone_consent: boolean;
  email_consent: boolean;
  only_important_updates: boolean;
  business_name: string | null;
  business_type: string | null;
  is_free_supplementary?: boolean;
  business_company_name: string | null;
  company_name: string | null;
  position: string | null;
  id_card_image: string | null;
  profile_image: string | null;
  created_by_id: number | null;
  created_by_name: string | null;
  updated_by_id: number | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

type Opt = { value: string; label: string };
const opts = (t: Translate, pairs: [string, TranslationKey][]): Opt[] =>
  pairs.map(([value, label]) => ({ value, label: t(label) }));

// Three, and the API's `NamePrefix` is the same three: a professional title —
// Dr., Atty., Engr. — is not an honorific, and no member had one. Offered
// here, it would be a choice the card cannot be embossed with and the enum
// refuses.
export const PREFIX = (t: Translate) =>
  opts(t, [
    ["mr", "mem.mr"],
    ["ms", "mem.ms"],
    ["mrs", "mem.mrs"],
  ]);
// Suffixes read the same in every language.
export const SUFFIX = (): Opt[] =>
  ["Sr", "Jr", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"].map((v) => ({
    value: v.toLowerCase(),
    label: v,
  }));
export const GENDER = (t: Translate) =>
  opts(t, [
    ["female", "mem.female"],
    ["male", "mem.male"],
  ]);
/** Every channel a member row can carry, label-mapped. Online is recorded by
 *  the SNR pull as well as keyed in at enrolment, so a row holding it still
 *  needs a label wherever the value is shown. */
export const ACQUISITION = (t: Translate) =>
  opts(t, [
    ["walk_in", "mem.walkIn"],
    ["canvassing", "mem.canvassing"],
    ["benefits", "mem.benefits"],
    ["online", "mem.online"],
    // The machines. Recorded by whatever carried the enrolment in, never taken
    // at a counter — but a member whose row came in under one has to read as
    // one, and the filters and the columns have to be able to say so.
    ["kiosk", "mem.kiosk"],
    ["kiosk_snrshopping", "mem.kioskSnrshopping"],
    ["kiosk_ub", "mem.kioskUb"],
  ]);
/**
 * The channels a person can be signed up through, which is what a club
 * corrects — and the whole of what the two counter ranks may set.
 */
export const COUNTER_CHANNELS = ["walk_in", "canvassing", "online"];

/**
 * The machines. Recorded by whatever carried the enrolment in, and set by hand
 * only by a Manager and above: a kiosk enrolment is a correction of an import,
 * not a day's work at a club, and the desk that takes a walk-in has no reason
 * to file one as a kiosk's. Legacy drew the same line —
 * `includeKioskAcquisitionTypes` was manager, superuser and owner.
 */
const KIOSK_CHANNELS = ["kiosk", "kiosk_snrshopping", "kiosk_ub"];

/** Whether this rank may file an enrolment as a machine's. */
export const setsKioskChannels = (rank: Rank) => atLeast(rank, "manager");

/**
 * Whether this rank gets the two bulk controls on the members toolbar: Export,
 * and the Batch Update menu beside it.
 *
 * The same line `setsKioskChannels` draws, for the same reason: both act on the
 * table rather than on a member, and both are a back-office job rather than a
 * counter's. Named once here so the toolbar and its test agree about where the
 * line is, and so moving it is one edit.
 */
export const worksInBulk = (rank: Rank) => atLeast(rank, "manager");

/**
 * The channels this rank may set on a member.
 *
 * One rule, read by every picker that asks the question: the acquisition sheet,
 * the amendments that land an account on Member tier, and the channel a card is
 * moved under. Benefits is nobody's — it is the other three tiers' channel and
 * these pickers are only ever a member's — and the kiosks are a Manager's and
 * above, which leaves an Associate or a Supervisor the three a club takes over
 * the counter.
 *
 * It is the list to *choose* from, not the list to *read* by: the columns, the
 * chips and the filter drawer keep every channel, or a member enrolled by a
 * kiosk would be unreadable and unfindable to the people who serve them.
 */
export const channelsFor = (t: Translate, rank: Rank, current?: string | null) =>
  ACQUISITION(t).filter(
    (option) =>
      option.value !== "benefits" &&
      (setsKioskChannels(rank) ||
        !KIOSK_CHANNELS.includes(option.value) ||
        // The one exception, and the reason this takes the account's own
        // channel: a member enrolled by a kiosk, opened by somebody who may
        // not file one, would otherwise meet a picker that cannot say what
        // happened — and every way out of it would be a lie about how that
        // member came in. It is theirs to keep, not theirs to set elsewhere.
        option.value === current),
  );
/** The three channels member creation offers. Benefits is the other tiers'
 *  channel and is filled in for them, never picked. */
const ADD_MEMBER_CHANNELS = ["walk_in", "canvassing", "online"];
export const ADD_MEMBER_ACQUISITION = (t: Translate) =>
  ACQUISITION(t).filter((o) => ADD_MEMBER_CHANNELS.includes(o.value));
/** What was bought. Only a member-tier account has one that means anything. */
export const MEMBERSHIP_TYPE = (t: Translate) =>
  opts(t, [
    ["gold", "mem.gold"],
    ["business", "mem.business"],
    ["unioil", "mem.unioil"],
    ["qsr", "mem.qsr"],
  ]);
/** Who the member is to S&R. With the account type it decides the card number. */
export const TIER = (t: Translate) =>
  opts(t, [
    ["member", "mem.member"],
    ["affiliates", "mem.affiliates"],
    ["employee", "mem.employee"],
    ["complimentary", "mem.complimentary"],
  ]);
export const ACCOUNT_TYPE = (t: Translate) =>
  opts(t, [
    ["primary", "mem.primary"],
    ["supplementary", "mem.supplementary"],
  ]);
/**
 * Where a member's co-brand (UnionBank) application stands, in the order of
 * the codes legacy keeps for them: 01 Pending, 02 Approved, 03 Declined. The
 * server matches each against the code as well as the status.
 */
export const CO_BRAND = (t: Translate) =>
  opts(t, [
    ["pending", "mem.pending"],
    ["approved", "mem.approved"],
    ["declined", "mem.declined"],
  ]);
export const STATUS = (t: Translate) =>
  opts(t, [
    ["active", "mem.active"],
    ["expired", "mem.expired"],
    ["cancelled", "mem.cancelled"],
    ["blacklisted", "mem.blacklisted"],
  ]);
const RELATION = (t: Translate) =>
  opts(t, [
    ["spouse", "mem.spouse"],
    ["child", "mem.child"],
    ["parent", "mem.parent"],
    ["sibling", "mem.sibling"],
    ["other", "mem.other"],
  ]);

export const label = (options: Opt[], value: string | null) =>
  options.find((o) => o.value === value)?.label ?? value ?? "—";
export const day = (value: string | null) =>
  value ? format(new Date(value), "MM/dd/yyyy") : "—";

/** The name as one line. There is no full_name column: the parts are the name. */
export const fullName = (m: {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
}) => [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");

/** The name as the card carries it: the full name, then the suffix in capitals
 *  (GREY SMOKE II). The card and the member page's heading carry the suffix;
 *  Full Name and every other place a member is named read `fullName`. */
export const nameWithSuffix = (m: {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  suffix?: string | null;
}) => [fullName(m), m.suffix?.toUpperCase()].filter(Boolean).join(" ");

/**
 * The Card Name line. A stored name that is just the three parts was derived
 * before the suffix went on cards, so it reads with the suffix now; one typed
 * by hand is shown as typed.
 */
export const cardNameOf = (m: {
  card_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  suffix?: string | null;
}) =>
  !m.card_name || m.card_name.toUpperCase() === fullName(m).toUpperCase()
    ? nameWithSuffix(m)
    : m.card_name;

// ---------------------------------------------------------------------------
// The form: every field from the design, grouped the way the screen groups them.
// ---------------------------------------------------------------------------
export const fields = (t: Translate, rank: Rank = "super_user"): FieldDef[] => [
  {
    section: t("mem.sectionIdentity"),
    name: "prefix",
    label: t("mem.prefix"),
    type: "select",
    options: PREFIX(t),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "suffix",
    label: t("mem.suffix"),
    type: "select",
    options: SUFFIX(),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "first_name",
    label: t("mem.firstName"),
    required: true,
    uppercase: true,
  },
  {
    section: t("mem.sectionIdentity"),
    name: "middle_name",
    label: t("mem.middleName"),
    uppercase: true,
  },
  {
    section: t("mem.sectionIdentity"),
    name: "last_name",
    label: t("mem.lastName"),
    uppercase: true,
  },
  {
    section: t("mem.sectionIdentity"),
    name: "gender",
    label: t("mem.gender"),
    required: true,
    type: "select",
    options: GENDER(t),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "date_of_birth",
    label: t("mem.dateOfBirth"),
    required: true,
    type: "date",
    // The same bounds the API enforces, so the picker cannot offer a refusal.
    min: birthDateMin(),
    max: birthDateMax(),
  },

  {
    section: t("mem.sectionAccount"),
    name: "account_number",
    label: t("mem.accNo"),
    required: true,
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "snr_account_number",
    label: t("mem.snrAccountNumber"),
  },
  {
    section: t("mem.sectionAccount"),
    name: "account_type",
    label: t("mem.accountType"),
    type: "select",
    options: ACCOUNT_TYPE(t),
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "account_status",
    label: t("mem.accountStatus"),
    type: "select",
    options: STATUS(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "membership_type",
    label: t("mem.membershipType"),
    type: "select",
    options: MEMBERSHIP_TYPE(t),
    required: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "membership_tier",
    label: t("mem.membershipTier"),
    required: true,
    type: "select",
    options: TIER(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "acquisition_type",
    label: t("mem.acquisitionType"),
    required: true,
    type: "select",
    // Benefits stays on this one: the form covers every tier, and the three
    // the club signs up itself are recorded under it — an edit that could not
    // show a complimentary member's own channel would be an edit that blanks
    // it. What the rank decides is the kiosks.
    options: ACQUISITION(t).filter(
      (option) => setsKioskChannels(rank) || !KIOSK_CHANNELS.includes(option.value),
    ),
  },
  {
    section: t("mem.sectionAccount"),
    name: "amendment",
    label: t("mem.amendment"),
    disabled: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "parent_account_number",
    label: t("mem.parentAccount"),
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "supplementary_account_number",
    label: t("mem.supplementaryAccount"),
    disabled: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "family_member_account_number",
    label: t("mem.familyMember"),
  },
  { section: t("mem.sectionAccount"), name: "employee_number", label: t("mem.employeeNumber") },
  {
    section: t("mem.sectionAccount"),
    name: "employee_relation",
    label: t("mem.employeeRelation"),
    type: "select",
    options: RELATION(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "volume_shopper",
    label: t("mem.volumeShopper"),
    type: "boolean",
  },
  {
    section: t("mem.sectionAccount"),
    name: "co_brand_tagging",
    label: t("mem.coBrandTagging"),
  },
  {
    section: t("mem.sectionAccount"),
    name: "union_bank_tag",
    label: t("mem.unionBankTag"),
    type: "select",
    options: CO_BRAND(t),
  },

  { section: t("mem.sectionCard"), name: "card_name", label: t("mem.cardName") },
  { section: t("mem.sectionCard"), name: "card_number", label: t("mem.cardNumber") },
  {
    section: t("mem.sectionCard"),
    name: "temporary_card_number",
    label: t("mem.temporaryCardNumber"),
  },
  {
    section: t("mem.sectionCard"),
    name: "issued_date",
    label: t("mem.issuedDate"),
    type: "date",
  },
  {
    section: t("mem.sectionCard"),
    name: "valid_until",
    label: t("mem.validUntil"),
    type: "date",
  },
  {
    section: t("mem.sectionCard"),
    name: "no_expiry",
    label: t("mem.noExpiry"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "is_print_ready",
    label: t("mem.isPrintReady"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "is_temporarily_active",
    label: t("mem.temporarilyActive"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "extended_time_minutes",
    label: t("mem.extendedTime"),
    type: "number",
    min: 0,
  },

  {
    section: t("mem.sectionClubsAddress"),
    name: "signup_club_id",
    label: t("mem.signupClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "active_club_id",
    label: t("mem.activeClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "renew_club_id",
    label: t("mem.renewClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  // The three that make an address an address. The enrolment form has asked
  // for all three since it was written — `NEEDS_ADDRESS` in `MemberNewPage` —
  // and an edit that could quietly drop one would be a way to end up with a
  // member the enrolment would not have accepted.
  //
  // They are starred rather than merely checked: a clerk should see which
  // fields the form will not let go of before they start typing, not find out
  // on save.
  {
    section: t("mem.sectionClubsAddress"),
    name: "city_id",
    label: t("mem.city"),
    type: "fk",
    endpoint: "/cities/compact",
    required: true,
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "barangay_id",
    label: t("mem.barangay"),
    type: "fk",
    endpoint: "/barangays/compact",
    dependsOn: "city_id",
    required: true,
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "address_street",
    label: t("mem.addressStreet"),
    required: true,
  },
  { section: t("mem.sectionClubsAddress"), name: "unit_number", label: t("mem.unitNumber") },
  {
    section: t("mem.sectionClubsAddress"),
    name: "building_name",
    label: t("mem.buildingName"),
  },

  {
    section: t("mem.sectionContactConsent"),
    name: "mobile_number",
    label: t("mem.mobileNumber"),
    type: "tel",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "email",
    label: t("mem.email"),
    type: "email",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "phone_consent",
    label: t("mem.phoneConsent"),
    type: "boolean",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "email_consent",
    label: t("mem.emailConsent"),
    type: "boolean",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "only_important_updates",
    label: t("mem.onlyImportantUpdates"),
    type: "boolean",
  },

  { section: t("mem.sectionBusiness"), name: "business_name", label: t("mem.businessName") },
  {
    // Configured, not typed: the line of business is a list somebody maintains
    // under Configuration, and free text left every member's answer spelled
    // its own way. The value is the name — the column holds the text, as the
    // card replacement reason does — so what is already on a member still
    // reads as itself even when it is not on the list any more.
    section: t("mem.sectionBusiness"),
    name: "business_type",
    label: t("mem.businessType"),
    type: "select",
    loadOptions: async (_parent, signal) =>
      (await listAll<{ name: string }>("/business-types", { sort: "name" }, signal)).map(
        (row) => ({ value: row.name, label: row.name }),
      ),
  },
  {
    section: t("mem.sectionBusiness"),
    name: "business_company_name",
    label: t("mem.businessCompanyName"),
  },
  { section: t("mem.sectionBusiness"), name: "company_name", label: t("mem.companyName") },
  { section: t("mem.sectionBusiness"), name: "position", label: t("mem.position") },

  {
    section: t("mem.sectionImages"),
    name: "id_card_image",
    label: t("mem.idCard"),
    type: "image",
  },
  {
    section: t("mem.sectionImages"),
    name: "profile_image",
    label: t("mem.profileImage"),
    type: "image",
  },
];

const FIELD_NAMES = fields((key) => key).map((f) => f.name);
export const toForm = (m: Member): Record<string, string> =>
  Object.fromEntries(
    FIELD_NAMES.map((name) => {
      const value = (m as unknown as Record<string, unknown>)[name];
      return [name, value === null || value === undefined ? "" : String(value)];
    }),
  );

/**
 * The members list as configuration. Exported so a test can read what the
 * screen claims about itself without rendering forty million rows.
 */
export const membersConfig = (
  t: Translate,
  rank: Rank = "super_user",
): ResourceConfig<Member> => ({
  title: t("nav.members"),
  singular: t("mem.singular"),
  endpoint: "/members",
  model: "member",
  // Newest first, off the primary key. `created_at` says the same thing a
  // beat slower — 34ms against 18ms on forty million rows — and needs its own
  // index to say it; `id` is the key every partition is already sorted by.
  defaultSort: { key: "id", order: "desc" },
  searchHistory: true,
  // No floor, which is what legacy searched on: the first character is a
  // prefix like any other — `j:*` off the same index `juan` reads — and the
  // API takes it now too. `SEARCH_MIN_LENGTH` is the other half of this.
  //
  // `MemberQuery.search` does refuse the 201st character, so the box stops at
  // 200 rather than send a paste out to be rejected; the saved-search POST
  // takes the same 200.
  searchMaxLength: 200,
  // The list follows the typing, after a pause — legacy's pattern, and what
  // the members list is built for: a name is matched by prefix off the GIN
  // index, and a term common enough to match half the table is exactly the
  // term whose first page fills fastest. Enter still files the term, so what
  // is remembered is what somebody settled on rather than every prefix typed
  // on the way to it.
  //
  // Recent searches follow the account, not the browser. The whole list once,
  // then held in the browser and matched there; every read after that asks
  // only for what changed. Typing costs no history request; committing does,
  // and that one answers with whatever else changed, so the copy stays in step.
  searchHistoryStore: {
    load: async (since, signal) =>
      (
        await api.get<SearchTerm[]>("/members/search-history", {
          ...(since ? { params: { since } } : {}),
          ...(signal ? { signal } : {}),
        })
      ).data,
    remember: async (term, since) =>
      (await api.post<SearchTerm[]>("/members/search-history", { term, since })).data,
    forget: (id) => api.delete(`/members/search-history/${id}`),
  },
  // Downloads are `member_<date>.csv`, the same name the server's export uses.
  csvBase: "member",
  // The filters go as a body: no query-string limit, lists carried as lists.
  listMethod: "post",
  // Twenty-eight million rows are not counted for every page; past this it
  // reads "10,000+". The same number as `COUNT_CAP` in the members service, and
  // it has to stay that way: it is what decides whether a total is shown as a
  // floor. Lower than the server's and an exact total reads as "9,999+"; higher
  // and a floor reads as exact.
  //
  // It is where the advanced filter's speed comes from: the count is the whole
  // of the wait, and reading a hundred thousand matching rows took 12.5s on a
  // status-and-tier-and-club filter against 83ms for ten thousand.
  countCap: 10_000,
  filterLabel: t("mem.advancedFilter"),
  // Legacy's shape on a narrow frame: search and reload, then the funnel with
  // Saved Filters and the rest beside it, then Add Member on its own line. The
  // wide row is untouched — `stacked` only applies once the frame is tight.
  toolbarLayout: "stacked",
  // The action row is worded end to end. Refresh is the one control whose
  // glyph says the whole thing, and the row is long enough to want the width.
  iconRefresh: true,
  projectFields: true,
  viewColumn: "last_name",
  // The name is the way in, and on this table it is often not on screen: there
  // are twenty-odd columns to choose from and the list scrolls sideways, so by
  // the time the column somebody is reading is in view the name has gone off
  // the left edge. A double-click on whichever cell they are already looking
  // at opens the same record.
  openOnRowDoubleClick: true,
  noRowMenu: true,
  // A membership is a record of something that happened, so nothing destroys
  // one: Delete takes it off the lists and leaves the row where it is, with the
  // log, the card history and the six per-member tables still pointing at it.
  //
  // No archive view and no restore here, unlike the place lists. A member is
  // removed because they should not be on the list — not filed for later — and
  // an archive nobody is meant to work from is a second members list to keep
  // paged, searched and permissioned for nothing. Ticking rows deletes a page
  // of them at once.
  bulk: { permission: "delete__member" },
  confirmDeleteMessage: t("mem.confirmDelete"),
  // The whole page, like every other record — not a sheet over the list. The
  // id is in the address bar, obfuscated, so a reload comes back here.
  renderView: (m, ctx) => <MemberView member={m} ctx={ctx} />,
  // Manager and above. The file is the whole list — every match, every column
  // the screen has on — and a member row carries a name, a birthday and two
  // contact numbers, so the button that takes twenty-eight million of them out
  // of the system is not one an Associate or a Supervisor is handed on the way
  // past. They still have the list itself, searched and filtered, which is what
  // the desk works from. The API asks only for `view__member`, so this is a
  // button hidden rather than a right withdrawn — same as `setsKioskChannels`.
  exportable: worksInBulk(rank),
  formColumns: 2,
  addLabel: t("mem.addMember"),
  // Just the primary action. `CsvMenus` is the `toolbar` below, and having it
  // here as well drew Batch Upload and Batch Update twice in the same row.
  addMenu: <AddMemberMenu />,
  // Any one of the enrolments opens the button; the menu inside then lists only
  // the ones this person may use. Left at the default `add__member` it asked
  // for the ordinary tier alone, so somebody given employees and nothing else
  // found no button at all rather than a menu with employees on it.
  // Supplementary is here for the same reason it is a grant: it is a way of
  // taking somebody on, even though the menu offers it from a member's own page.
  addPermissions: [
    ...TIERS.map((tier) => tier.permission),
    "add__member_supplementary_history",
  ],
  // Everything except the eight legacy opens on: a name, the two numbers a
  // member is looked up by, what they bought, what they are, when it runs out
  // and where it stands. The rest is a click away in Customize Column, in the
  // order the columns are listed there.
  hiddenByDefault: [
    "mobile_number",
    "created_at",
    "created_by_id",
    "updated_by_id",
    "updated_at",
    "signup_club",
    "prefix",
    "gender",
    "city",
    "barangay",
    "email_consent",
    "phone_consent",
    "only_important_updates",
    "account_type",
    "unit_number",
    "building_name",
    "business_name",
    "active_club",
    "renew_club",
    "card_name",
    "email",
    "snr_account_number",
  ],
  columns: [
    {
      key: "last_name",
      fields: ["first_name", "middle_name", "last_name"],
      label: t("mem.fullName"),
      sortKey: "last_name",
      // One line, whatever the name. A four-word membership name wrapped to
      // three lines and took the whole row with it, so a page of members was
      // read at three different row heights.
      className: "max-w-64",
      render: (m) => (
        <span className="block truncate underline underline-offset-4" title={fullName(m)}>
          {fullName(m)}
        </span>
      ),
    },
    {
      key: "date_of_birth",
      label: t("mem.dateOfBirth"),
      sortKey: "date_of_birth",
      render: (m) => day(m.date_of_birth),
    },
    {
      key: "mobile_number",
      label: t("mem.mobileNumber"),
      render: (m) => m.mobile_number ?? "—",
    },
    {
      key: "account_number",
      label: t("mem.accountNumber"),
      sortKey: "account_number",
      render: (m) => m.account_number,
    },
    {
      // Enrolment day. There is no `member_since` column — the day the row was
      // written is the day the member joined, and a second column saying so is
      // a second column that can disagree — so the label is the legacy one and
      // the field underneath is `created_at`. It is the list's default sort,
      // and `ix_members_created_at_id` is the index that serves it.
      key: "created_at",
      sortKey: "created_at",
      label: t("mem.memberSince"),
      render: (m) => fmtDate(m.created_at),
      csv: (m) => m.created_at,
    },
    {
      // Keyed on the column the server names, so the audit column `ResourcePage`
      // appends to every screen dedupes against this one instead of doubling it.
      key: "created_by_id",
      fields: ["created_by_name"],
      label: t("mem.createdBy"),
      render: (m) => m.created_by_name ?? "—",
      csv: (m) => m.created_by_name,
    },
    {
      key: "updated_by_id",
      fields: ["updated_by_name"],
      label: t("mem.modifiedBy"),
      render: (m) => m.updated_by_name ?? "—",
      csv: (m) => m.updated_by_name,
    },
    { key: "updated_at", label: t("mem.modifiedAt"), render: (m) => fmtDate(m.updated_at) },
    {
      key: "signup_club",
      fields: ["signup_club_name"],
      label: t("mem.signUpClub"),
      render: (m) => m.signup_club_name ?? "—",
      csv: (m) => m.signup_club_name,
    },
    { key: "prefix", label: t("mem.prefix"), render: (m) => label(PREFIX(t), m.prefix) },
    { key: "gender", label: t("mem.gender"), render: (m) => label(GENDER(t), m.gender) },
    {
      key: "city",
      label: t("mem.city"),
      fields: ["city_name"],
      render: (m) => m.city_name ?? "—",
      csv: (m) => m.city_name,
    },
    {
      key: "barangay",
      fields: ["barangay_name"],
      label: t("mem.barangay"),
      render: (m) => m.barangay_name ?? "—",
      csv: (m) => m.barangay_name,
    },
    {
      key: "membership_type",
      label: t("mem.membershipType"),
      render: (m) => label(MEMBERSHIP_TYPE(t), m.membership_type),
    },
    {
      key: "email_consent",
      label: t("mem.isEmailConsent"),
      render: (m) => (m.email_consent ? t("rp.yes") : t("rp.no")),
    },
    {
      key: "phone_consent",
      label: t("mem.isPhoneConsent"),
      render: (m) => (m.phone_consent ? t("rp.yes") : t("rp.no")),
    },
    {
      key: "only_important_updates",
      label: t("mem.onlyImportantUpdates"),
      render: (m) => (m.only_important_updates ? t("rp.yes") : t("rp.no")),
    },
    {
      key: "account_type",
      label: t("mem.accountType"),
      render: (m) => label(ACCOUNT_TYPE(t), m.account_type),
    },
    {
      key: "membership_tier",
      label: t("mem.membershipTier"),
      render: (m) => label(TIER(t), m.membership_tier),
    },
    { key: "unit_number", label: t("mem.unitNumber"), render: (m) => m.unit_number ?? "—" },
    {
      key: "building_name",
      label: t("mem.buildingName"),
      render: (m) => m.building_name ?? "—",
    },
    {
      key: "business_name",
      label: t("mem.businessName"),
      render: (m) => m.business_name ?? "—",
    },
    {
      key: "active_club",
      fields: ["active_club_name"],
      label: t("mem.activeClub"),
      render: (m) => m.active_club_name ?? "—",
      csv: (m) => m.active_club_name,
    },
    {
      key: "renew_club",
      fields: ["renew_club_name"],
      label: t("mem.renewClub"),
      render: (m) => m.renew_club_name ?? "—",
      csv: (m) => m.renew_club_name,
    },
    {
      key: "card_name",
      // The name parts too: a card name stored before the suffix went on cards
      // reads with it, as on the detail page — see `cardNameOf`.
      fields: ["card_name", "first_name", "middle_name", "last_name", "suffix"],
      label: t("mem.cardName"),
      render: (m) => cardNameOf(m) || "—",
    },
    {
      key: "card_number",
      label: t("mem.cardNumber"),
      sortKey: "card_number",
      render: (m) => m.card_number ?? "—",
    },
    {
      key: "valid_until",
      label: t("mem.validUntil"),
      sortKey: "valid_until",
      render: (m) => day(m.valid_until),
    },
    {
      key: "account_status",
      label: t("mem.accountStatus"),
      render: (m) => (
        <Badge tone={m.account_status === "active" ? "success" : "neutral"}>
          {label(STATUS(t), m.account_status)}
        </Badge>
      ),
      csv: (m) => m.account_status,
    },
    { key: "email", label: t("mem.email"), render: (m) => m.email ?? "—" },
    {
      key: "snr_account_number",
      label: t("mem.snrAccountNumber"),
      render: (m) => m.snr_account_number ?? "—",
    },
  ],
  // Not a filter UI — `renderFilters` below takes that over — but what the
  // applied-filter chips are read through. A chip resolves its label and its
  // value against this list, so a filter missing from it renders as the raw
  // thing in the query: "Signup club 6", "Status expired".
  //
  // Derived from the Advanced Filter's own groups rather than restated, so a
  // filter added there is named here without anyone remembering to. Only the
  // two kinds that need resolving: `fk` turns an id into a club or a person,
  // `select` turns a stored enum value into the words the dropdown showed.
  filters: chipFilters(t),
  renderFilters: (values, onApply, onClose) => (
    <AdvancedFilter values={values} onApply={onApply} onClose={onClose} />
  ),
  // The saved sets live beside the filter button, so applying one is one
  // click rather than a trip through the drawer.
  filterExtra: (values, onApply) => <SavedFiltersDropdown values={values} onApply={onApply} />,

  fields: fields(t, rank),
  toForm,
  newDefaults: (profile) => ({
    account_type: "primary",
    account_status: "active",
    signup_club_id: profile.club?.id.toString() ?? "",
    active_club_id: profile.club?.id.toString() ?? "",
  }),
  // Two solid buttons side by side leave the eye no answer about which one to
  // press, so only Add Member is filled and the CSV flows are secondary. One
  // menu each: enrolling a file of new members and correcting a field on
  // existing ones are two different jobs, and the row has the width to say so.
  toolbar: <CsvMenus />,
  // A place rather than an action, but still a button on the row: nothing here
  // hides behind a menu.
  menuExtra: <ActivityLogButton />,
});

/** What the export button is downloading, while it downloads. */
interface ExportRun {
  params: ListParams;
  /**
   * The columns Customize Columns has on, in table order, each carrying the
   * API fields behind it. The whole of the column choice: it is asked once, on
   * the screen, and the file is the answer.
   */
  columns: { key: string; label: string; fields: string[] }[];
}

function MembersPage() {
  const t = useTranslation();
  /**
   * The export in flight, if there is one. Set by the button and cleared when
   * the file arrives, fails, or is stopped.
   *
   * There is nothing to ask before it starts. The columns are the screen's
   * already and the filters are the list's already, so a dialog between the
   * click and the download was a form whose every field was filled in — and
   * one of them, the column list, asked a second time for something Customize
   * Columns had just been used to say.
   */
  const [running, setRunning] = useState<ExportRun | null>(null);
  // The edit form's channel list is the rank's; see `channelsFor`.
  const rank = useRank();
  const base = useMemo(() => membersConfig(t, rank), [t, rank]);
  const config = useMemo(
    () =>
      ({
        ...base,
        exportAll: ({ params, columns }) => setRunning({ params, columns }),
      }) as ResourceConfig<Member>,
    [base],
  );
  return (
    <>
      <ResourcePage config={config} />
      {running ? <ExportRunner run={running} onDone={() => setRunning(null)} /> : null}
    </>
  );
}

const KB = 1024;
const MB = KB * KB;
/** Bytes as a person reads them. Below a megabyte the decimal is noise. */
const written = (bytes: number) =>
  bytes < MB
    ? `${Math.round(bytes / KB).toLocaleString()} KB`
    : `${(bytes / MB).toFixed(1)} MB`;

/** Elapsed as a clock: m:ss, which is how long a wait feels rather than 94800ms. */
const clock = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * The download, from the click to the file.
 *
 * There is no question in front of it. The columns are whatever Customize
 * Columns has on and the filters are whatever the list is showing, so the two
 * things a picker would have asked are already answered on the screen behind
 * it — and one of them it asked twice, which is how a column turned off on the
 * table could still arrive in the file, and a column turned on could not.
 *
 * What is left is worth a modal, because the wait is real: this is every
 * matching member and not a page of them. A size climbing beside a clock is
 * what says a two-minute download is progress rather than a hang, and Stop is
 * what makes a wrong one cost nothing. Nothing else dismisses it — no X, no
 * backdrop, no Escape — because every one of those abandons the download, and
 * a stray click beside a modal is the commonest accident there is.
 */
function ExportRunner({ run, onDone }: { run: ExportRun; onDone: () => void }) {
  const t = useTranslation();
  const [bytes, setBytes] = useState(0);
  // The click is when the wait started, not the first byte: the server spends
  // the opening seconds counting and planning, and a clock that skips them is
  // a clock that disagrees with the person watching it.
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  /** Aborting the request closes the connection, and the endpoint asks
   *  `request.is_disconnected` between batches — so Stop ends the server
   *  reading as well as the browser waiting, rather than leaving a cursor
   *  walking forty million rows for a file nobody will take. */
  const abort = useRef<AbortController | null>(null);

  // Half a second is under what a person reads as a lag and is a quarter of
  // the repaints of a 60fps one.
  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 500);
    return () => clearInterval(id);
  }, [startedAt]);

  const download = useMutation({
    mutationFn: async () => {
      // The list's own question, without the page: an export is every match.
      // `fields` is added back here and nowhere else — the list asks for every
      // column so that showing one costs no request, but the file holds the
      // columns the screen holds.
      const { page: _page, size: _size, ...filterParams } = run.params;
      const controller = new AbortController();
      abort.current = controller;
      const file = await api.post<Blob>(
        "/members/export",
        {
          ...filterParams,
          // One column can read more than one field — a club's name comes with
          // its id — and two columns can share one, so the set is taken rather
          // than the concatenation.
          fields: [...new Set(run.columns.flatMap((column) => column.fields))],
        },
        {
          responseType: "blob",
          signal: controller.signal,
          // No deadline. The instance gives every other request a minute, which
          // an export of forty million members passes while the server is still
          // streaming it correctly — and axios aborting mid-stream is exactly
          // the "export does not work" this endpoint was reported for.
          timeout: 0,
          // The response is streamed, so it carries no Content-Length and there
          // is no percentage to be had. `loaded` is real though, and a size
          // climbing beside a clock is what says the wait is progress and not
          // a hang.
          onDownloadProgress: (event) => setBytes(event.loaded),
        },
      );
      // Through the shared helper: this runs with the modal still up, and a
      // modal makes the rest of the document inert — a link appended to the
      // body there is clicked and nothing happens.
      downloadBlob(file.data, csvName("member"));
    },
    onSuccess: onDone,
    onError: (error) => {
      // A cancel is an answer, not a failure. There is no picker to land back
      // on now, so both endings close the modal and say what happened in a
      // line: the list is still there, and Export is one click away.
      if (axios.isCancel(error)) toast.info(t("mem.exportCancelled"));
      else toast.error(errorMessage(error, t("mem.exportFailed")));
      onDone();
    },
  });

  /**
   * Started on mount, because mounting is the click.
   *
   * The ref rather than the mutation's own state: React runs an effect twice
   * in development, and the second run would be a second export of forty
   * million rows.
   */
  const start = download.mutate;
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  return (
    <Modal
      open
      onClose={() => undefined}
      size="sm"
      dismissible={false}
      title={t("mem.exportRunningTitle")}
      description={t("mem.exportRunningBody")}
      footer={
        <Button variant="secondary" onClick={() => abort.current?.abort()}>
          {t("mem.exportStop")}
        </Button>
      }
    >
      <div aria-live="polite" className="pb-2">
        <p className="text-title-3 text-fg tabular-nums">{written(bytes)}</p>
        <p className="text-fg-muted text-callout mt-1 tabular-nums">
          {t("mem.exportElapsed").replace("{elapsed}", clock(elapsed))}
        </p>
        {/* Indeterminate on purpose: the response is streamed with no length,
            and a bar filling against a guessed total is a bar that lies. */}
        <div className="bg-surface-sunken mt-4 h-1 overflow-hidden rounded-full">
          <div className="bg-accent animate-sweep h-full w-1/3 rounded-full" />
        </div>
        <p className="text-fg-muted text-footnote mt-3">{t("mem.exportKeepOpen")}</p>
      </div>
    </Modal>
  );
}

/**
 * The Detail View as a page: the way back, and the member's own body.
 *
 * No Refresh or Edit in the header — the body carries both already, a refresh
 * beside the card and a pencil on each section it can edit, and a second pair
 * above them was two of everything.
 */
function MemberView({ member, ctx }: { member: Member; ctx: DetailContext }) {
  const t = useTranslation();
  return (
    <>
      <PageHeader
        back={
          <button type="button" onClick={ctx.onClose} className={backLinkClass}>
            <BackArrow label={t("mem.singular")} />
          </button>
        }
        eyebrow={t("mem.singular")}
        title={
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="min-w-0 truncate">
              {fullName(member) || t("mem.memberN").replace("{id}", String(member.id))}
            </span>
            <span className="bg-surface-inset text-fg-muted text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
              {member.account_number}
            </span>
          </span>
        }
      />
      <MemberDetail id={member.id} embedded />
    </>
  );
}

// ---------------------------------------------------------------------------
// CSV menus: Batch Update (one file per action) and Bulk Upload (one per tier),
// each item with a sample template to start from.
// ---------------------------------------------------------------------------
interface CsvItem {
  key: string;
  label: TranslationKey;
  /**
   * The codename this one file asks for, beyond the group's own.
   *
   * An enrolment template is a tier, and a CSV of employees is still a pile of
   * employees — so a template offers itself only to whoever could enrol that
   * tier one at a time. The batch-update files have none: they change members
   * who already exist, which is the group's `change__member`.
   */
  permission?: string;
  /** The route below /members that serves the template and takes the file. */
  path: string;
  /**
   * The header the file must carry, as the template writes it.
   *
   * Written here rather than read off the template: it is static, so fetching
   * it was a request made only to learn something already known. Matching is
   * by field — case, spaces and underscores folded — so the two drifting in
   * spelling cannot break a file, only a column genuinely going missing can.
   */
  columns: string[];
  /** The batch updates report themselves as they run; enrolment answers once. */
  streams?: boolean;
  hint: TranslationKey;
  /** `hint` only: the {extra} slot. */
  extra?: TranslationKey;
  file: string;
  /** What the result counts. */
  noun: "updated" | "created";
}

const BATCH: CsvItem[] = [
  {
    key: "volume_shopper",
    label: "mem.volumeShopper",
    path: "batch-update/volume-shopper",
    streams: true,
    columns: ["Member Card", "Volume Shopper"],
    hint: "mem.hintYesNo",
    file: "member-bulk-update-volume-shopper-sample.csv",
    noun: "updated",
  },
  {
    key: "cancellation",
    label: "mem.cancellation",
    path: "batch-update/cancellation",
    streams: true,
    columns: ["Member Card", "Amendment Cancellation", "Reason"],
    hint: "mem.hintCancellation",
    file: "member-bulk-update-cancellation-sample.csv",
    noun: "updated",
  },
  {
    key: "valid_until",
    label: "mem.validUntil",
    path: "batch-update/valid-until",
    streams: true,
    columns: ["Member Card", "Date To"],
    hint: "mem.hintDate",
    file: "member-bulk-update-valid-until-sample.csv",
    noun: "updated",
  },
];

/** Every enrolment template starts and ends the same; only the middle differs. */
const UPLOAD_LEAD = [
  "Prefix",
  "First Name",
  "Middle Name",
  "Last Name",
  "Date of Birth",
  "Gender",
  "Mobile Number",
  "Email",
];
const UPLOAD_TAIL = [
  "Email Consent",
  "Phone Consent",
  "Only Receive Important Updates",
  "Agree to Terms",
  "Agree to Personal Info Usage",
  "Suffix",
];

/**
 * The three enrolment templates, exported so a test can write a file with the
 * columns the screen actually asks for rather than a guess at them.
 */
export const UPLOAD: CsvItem[] = (
  [
    [
      "employee",
      "mem.employee",
      "mem.hintEmployee",
      [
        "Unit Number",
        "Building Name",
        "Address/Street",
        "City",
        "Barangay",
        "Employee ID Number",
      ],
    ],
    ["affiliates", "mem.affiliates", "mem.hintAffiliates", ["Company Name"]],
    ["complimentary", "mem.complimentary", "mem.hintComplimentary", ["S&R Family Owner"]],
  ] as const
).map(([key, label, extra, middle]) => ({
  key,
  label,
  // `add__member_employee`, `add__member_affiliates`, `add__member_complimentary`
  // — the same three the Add Member menu asks for, because this is the same act
  // in bulk. Without this, a tier refused one at a time could be uploaded by
  // the hundred.
  permission: `add__member_${key}`,
  path: `upload/${key}`,
  columns: [...UPLOAD_LEAD, ...middle, ...UPLOAD_TAIL],
  hint: "mem.hintUpload",
  extra,
  file: `member-batch-upload-${key}-sample.csv`,
  noun: "created",
}));

/**
 * The keyboard a column deserves, and nothing more.
 *
 * The grid was drawing a select for every fixed-choice column and a date
 * picker for every date. Legacy draws one plain text box for every column of
 * every template, and it is right to: a spreadsheet exported from somewhere
 * else holds whatever it holds, and a control that only speaks its own
 * vocabulary either blanks a cell it cannot represent or argues with a value
 * the server would have accepted. The server is what judges a cell; the grid
 * shows what is in it.
 *
 * What survives is the hint a phone keyboard takes — digits for a card number,
 * a telephone pad for a mobile — which changes nothing about what may be typed.
 */
const COLUMN_TYPES: Record<string, CellKind> = {
  "Member Card": { kind: "text", inputMode: "numeric" },
  "Mobile Number": { kind: "text", inputMode: "tel" },
  Email: { kind: "text", inputMode: "email" },
  // The primary's account number, despite the name.
  "S&R Family Owner": { kind: "text", inputMode: "numeric" },
};

/**
 * How many rows a grouped error names before it stops counting them.
 *
 * Five, as legacy's list does: enough to go and look at one, short enough that
 * a group of four hundred is still one line.
 */
const ERROR_ROWS_SHOWN = 5;

/** The column heading a `columnKey`-shaped field name came from. */
function labelFor(field: string, columns: string[]): string | undefined {
  return columns.find((column) => columnKey(column) === field);
}

interface CsvResult {
  updated?: number;
  created?: number;
  /** Batch updates only; a bulk enrolment answers with `created` and nothing else. */
  counts?: { total: number; succeeded: number; failed: number; unchanged: number };
  skipped: {
    row: number;
    member_card: string | null;
    error: string;
    /** The column the problem is in, as `columnKey` writes it — so the grid can
     *  point at the cell. The first of `fields`; absent when the problem
     *  belongs to no column. */
    field?: string | null;
    /** Every column the problem is about. A duplicate identity is the first
     *  name, the last name and the birth date together — marking one of the
     *  three would say the other two are fine. */
    fields?: string[] | null;
    /** The row's own cells, so a rejected row can be corrected without the file. */
    values: Record<string, string>;
  }[];
}

/**
 * The tiers a person can enrol from the list; each opens the enrolment flow.
 *
 * One codename each, and each stands alone: a role may be given affiliates and
 * nothing else. `add__member` is not a base right the others sit on top of — it
 * means "may enrol an ordinary member", which is why it sits on that row like
 * any other.
 */
export const TIERS: {
  tier: "member" | "affiliates" | "employee" | "complimentary";
  label: TranslationKey;
  permission: string;
}[] = [
  { tier: "member", label: "mem.member", permission: "add__member" },
  { tier: "affiliates", label: "mem.affiliates", permission: "add__member_affiliates" },
  { tier: "employee", label: "mem.employee", permission: "add__member_employee" },
  {
    tier: "complimentary",
    label: "mem.complimentary",
    permission: "add__member_complimentary",
  },
];

function AddMemberMenu() {
  const t = useTranslation();
  const { id, button, panelProps, close } = useAnchoredPopover(200);
  const navigate = useNavigate();
  const allow = useCan();
  // Only the tiers this person may enrol. A tier left on the menu is one whose
  // form fills in and then refuses at the end, which is the worst moment to
  // find out. Nothing to offer at all takes the button with it.
  const tiers = TIERS.filter((item) => allow(item.permission));
  if (tiers.length === 0) return null;
  return (
    <>
      <Button ref={button} popoverTarget={id}>
        <Plus aria-hidden /> {t("mem.addMember")}
        <ChevronDown aria-hidden />
      </Button>
      <div {...panelProps}>
        {tiers.map((item) => (
          <button
            key={item.tier}
            type="button"
            onClick={() => {
              close();
              void navigate(`/members/new?tier=${encodeURIComponent(item.tier)}`);
            }}
            className={menuItemClass()}
          >
            {t(item.label)}
          </button>
        ))}
      </div>
    </>
  );
}

/** A function, so the page config above can name it before the item lists exist. */
/**
 * The two CSV flows, a button each. An upload enrols people who are not in the
 * table yet and an update corrects a field on people who are — two rights as
 * well as two jobs, so each menu shows to whoever holds the one behind it.
 */
const CSV_GROUPS: {
  label: TranslationKey;
  permission?: string;
  /** A floor as well as a codename, where the right alone is not the whole rule. */
  rank?: (rank: Rank) => boolean;
  items: CsvItem[];
}[] = [
  // No blanket right over the upload group: each template under it carries its
  // own tier codename, and asking for `add__member` as well would shut out
  // somebody given the affiliates batch and nothing else.
  { label: "mem.batchUpload", items: UPLOAD },
  // `change__member` is the right to correct a member, which a Supervisor holds
  // and uses all day on one record at a time. This menu is that right pointed at
  // a file: one upload re-cards, re-clubs or expires thousands of rows at once,
  // and there is no undo on the other side of it. Manager and above, like
  // Export beside it — see `worksInBulk`.
  {
    label: "mem.batchUpdate",
    permission: "change__member",
    rank: worksInBulk,
    items: BATCH,
  },
];

/**
 * A top-layer popover anchored under a button: the toolbar never clips it.
 * The same shape serves every menu on this page.
 */
function useAnchoredPopover(minWidth: number) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const place = () => {
    const anchor = button.current?.getBoundingClientRect();
    const panel = menu.current;
    if (!anchor || !panel) return;
    // Right-aligned to its button when that keeps it on screen.
    const width = Math.max(anchor.width, minWidth);
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
    Object.assign(panel.style, {
      top: `${anchor.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    });
  };
  const panelProps = {
    ref: menu,
    id,
    popover: "auto" as const,
    onToggle: (event: React.ToggleEvent<HTMLDivElement>) =>
      event.newState === "open" && place(),
    className:
      "border-line bg-surface-raised animate-sheet fixed m-0 rounded-xl border p-1 shadow-[var(--shadow-float)]",
  };
  return { id, button, menu, panelProps, close: () => menu.current?.hidePopover() };
}

function CsvMenus() {
  return (
    <>
      {CSV_GROUPS.map((group) => (
        <CsvMenu key={group.label} group={group} />
      ))}
    </>
  );
}

/** One flow's button and the files under it. Nothing at all without the right. */
function CsvMenu({ group }: { group: (typeof CSV_GROUPS)[number] }) {
  const t = useTranslation();
  const { id, button, panelProps, close } = useAnchoredPopover(200);
  const [open, setOpen] = useState<CsvItem | null>(null);
  const allow = useCan();
  const rank = useRank();
  // The group's right opens the menu; each file may ask for one of its own on
  // top. A menu whose every file is refused is a button that only disappoints,
  // so it goes with them.
  const items = group.items.filter((item) => !item.permission || allow(item.permission));
  if (group.rank && !group.rank(rank)) return null;
  if ((group.permission && !allow(group.permission)) || items.length === 0) return null;

  return (
    <>
      <Button ref={button} variant="secondary" popoverTarget={id}>
        {t(group.label)} <ChevronDown aria-hidden />
      </Button>
      <div {...panelProps}>
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              close();
              setOpen(item);
            }}
            className={menuItemClass()}
          >
            {t(item.label)}
          </button>
        ))}
      </div>
      {open ? (
        <CsvDialog title={t(group.label)} item={open} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}

/**
 * The member log, which is a place rather than an action — but a button on the
 * row all the same, outlined in the accent: it is the one control here that
 * leaves the screen. Hidden outright from anybody who may not read the log,
 * rather than shown and refused on arrival.
 */
function ActivityLogButton() {
  const t = useTranslation();
  const allow = useCan();
  if (!allow("view__member_log")) return null;
  return (
    <Button asChild variant="secondary" className="border-accent text-accent">
      <Link to="/member-activity-log">{t("page.activityLog")}</Link>
    </Button>
  );
}

/** How far a run has got, as the last frame told it. */
interface Progress {
  done: number;
  total: number;
  succeeded: number;
  unchanged: number;
  failed: number;
}

interface Finished {
  counts: { total: number; succeeded: number; failed: number; unchanged: number };
  updated: number;
  stopped: boolean;
}

interface AppliedRow {
  row: number;
  member_card: string;
  value: string;
}

/** A row on the receipt: what was in the file, and what became of it. */
interface ResultRow {
  /** The line in the posted file, header counted — what the server reported. */
  line: number;
  cells: string[];
  /**
   * Everything wrong with it, each with the column it names where it names
   * one. A list rather than one: a row with a bad birth date and a duplicate
   * employee number has two problems, and the grid shows each against the
   * cell it belongs to — keeping only the first hides the second until the
   * first is fixed and the file is sent again.
   */
  errors?: { message: string; fields: string[] }[];
}

/**
 * The receipt, split the way a person acts on it: what is done, and what is
 * still theirs to deal with.
 *
 * Every `row` the server reports counts the header — it enumerates from 2 — so
 * `sent[row - 2]` is the row somebody typed. `sent` is what was *posted*, which
 * for "this page" is one page and not the sheet, and that is the whole reason
 * it is held: without it a result's line number indexes into the wrong array.
 *
 * A streaming run names the rows it applied. Bulk enrolment answers with a
 * count and the failures only, so there the successes are what is left once the
 * failures are taken out — the same set, arrived at from the other side.
 *
 * Pending is two things, because they are one job: rows that were refused, and
 * — only when a single page went — rows never tried. One of them being a
 * receipt and the other an omission nobody is told about is how a file ends up
 * half done.
 */
export function splitOutcome({
  header,
  sent,
  applied,
  skipped,
  streams,
  sheetRows,
  partial,
}: {
  header: string[];
  sent: string[][];
  applied: AppliedRow[];
  skipped: CsvResult["skipped"];
  streams: boolean;
  sheetRows: string[][];
  partial: boolean;
}): { succeeded: ResultRow[]; pending: ResultRow[] } {
  const failures = new Set(skipped.map((row) => row.row));
  const cellsFor = (line: number, fallback: Record<string, string>): string[] =>
    // A row refused before the grid ever held it still carries its own cells,
    // which is what `correctFailed` rebuilds from.
    sent[line - 2] ?? header.map((column) => fallback[columnKey(column)] ?? "");

  const succeeded = (
    streams
      ? applied.map((row) => row.row)
      : sent.map((_, index) => index + 2).filter((line) => !failures.has(line))
  ).map((line) => ({ line, cells: cellsFor(line, {}) }));

  const sentRows = new Set(sent);
  // One entry per line. The server reports at most one error per row — every
  // check either `continue`s or `setdefault`s — but this is the place a slip
  // would turn into damage: a line listed twice is the same member in the grid
  // twice, and a resubmit enrols them twice. Line 0 is exempt, because it is
  // not a line — it is "could not be tied to one", and two of those are two
  // different problems, not one row twice.
  // One entry per line, carrying every complaint made about that line. The
  // server may report a row more than once — a bad date and a duplicate number
  // are two answers about one member — and a line listed twice here is the
  // same member in the grid twice, which a resubmit would enrol twice. Line 0
  // is exempt, because it is not a line: it is "could not be tied to one", and
  // two of those are two problems rather than one row twice.
  const byLine = new Map<number, ResultRow>();
  const unplaced: ResultRow[] = [];
  for (const row of skipped) {
    // Every column the server named, or the one it named, or none.
    const complaint = {
      message: row.error,
      fields: row.fields?.length ? row.fields : row.field ? [row.field] : [],
    };
    if (row.row === 0) {
      unplaced.push({ line: 0, cells: cellsFor(0, row.values), errors: [complaint] });
      continue;
    }
    const held = byLine.get(row.row);
    if (held) held.errors?.push(complaint);
    else
      byLine.set(row.row, {
        line: row.row,
        cells: cellsFor(row.row, row.values),
        errors: [complaint],
      });
  }
  const pending = [
    ...unplaced,
    ...byLine.values(),
    ...(partial
      ? sheetRows
          .map((cells, index) => ({ cells, line: index + 2 }))
          .filter((row) => !sentRows.has(row.cells))
      : []),
  ].sort((a, b) => a.line - b.line);

  return { succeeded, pending };
}

function CsvDialog({
  title,
  item,
  onClose,
}: {
  title: string;
  item: CsvItem;
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  /** Whether anything was written, so closing knows the list behind is stale. */
  const landed = useRef(false);
  const close = () => {
    if (landed.current) void queryClient.invalidateQueries({ queryKey: ["/members"] });
    onClose();
  };
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The file read into memory: its header, and the rows under it. */
  const [sheet, setSheet] = useState<{ header: string[]; rows: string[][] } | null>(null);
  const [editing, setEditing] = useState(false);
  /** Which page of the review grid is shown; the file is read a hundred at a time. */
  const [page, setPage] = useState(1);
  // What the run has said so far. The failures are kept as state because the
  // banner counts them; the applied rows only feed the settle at the end of
  // `send`, which reads its own accumulators.
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [skipped, setSkipped] = useState<CsvResult["skipped"]>([]);
  const [finished, setFinished] = useState<Finished | null>(null);
  /** Why each row still in the grid was refused — and in which column, when
   *  the answer names one — aligned to `sheet.rows`. A row that was simply
   *  never sent has none. */
  const [rowErrors, setRowErrors] = useState<(RowError[] | undefined)[]>([]);
  /** Every row that has gone through, across runs: fix-and-resend sends only
   *  the remainder, so the record of what is already done has to live here. */
  const [done, setDone] = useState<string[][]>([]);
  /** Which half of the review screen is open. */
  const [tab, setTab] = useState<"pending" | "success">("pending");
  /** The Success tab's own page, so flipping tabs does not lose either place. */
  const [donePage, setDonePage] = useState(1);
  const running = useRef<AbortController | null>(null);

  // Checked against the columns the item declares. No request: the header is
  // static, so fetching the template to read it was a round trip to learn
  // something already known.
  const missing = sheet ? missingColumns(sheet.header, item.columns) : [];

  // Written here rather than fetched: the header is already declared above,
  // and a file this screen can spell out in full is not worth a round trip.
  // The header and nothing else — an example row left in a template is a row
  // somebody uploads, and in a batch update that is a real member's card.
  const template = () => downloadText(toCsv([item.columns]), item.file);

  const told = (count: number) =>
    t(item.noun === "updated" ? "mem.membersUpdated" : "mem.membersCreated").replace(
      "{count}",
      String(count),
    );

  /**
   * Take a file: read it, and show its rows.
   *
   * The same act from the empty dialog and from the row above the table, so
   * "change the file" means exactly what "choose a file" meant.
   */
  const take = (chosen: File | null) => {
    setFile(chosen);
    setSheet(null);
    setFinished(null);
    setSkipped([]);
    setProgress(null);
    setRowErrors([]);
    setDone([]);
    setTab("pending");
    setEditing(false);
    setPage(1);
    if (!chosen) return;
    // Read as bytes and decoded here, not through `File.text()`: an Excel CSV
    // is Windows-1252, and as UTF-8 its `Ñ` arrives as `\uFFFD` — in the grid
    // below and, since the send is built from these rows, in the record too.
    void readCsvFile(chosen).then((text) => {
      const [header = [], ...rows] = parseCsv(text);
      setSheet({ header, rows });
      // Straight into the rows, as legacy does: a file is chosen in order to
      // be looked at, and a second click to see it is a click between the
      // person and the thing they came for.
      if (missingColumns(header, item.columns).length === 0) setEditing(true);
    });
  };

  /**
   * Which file the table is showing, above the table.
   *
   * With its own picker beside it, because the answer to "this is the wrong
   * file" has to be within reach of the rows that said so — otherwise it is
   * Cancel, reopen, and find the menu item again.
   */
  const fileRow = (
    <div className="border-line flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2">
      <span className="text-callout text-fg flex min-w-0 items-center gap-2">
        <FileText className="text-fg-subtle size-4 shrink-0" aria-hidden />
        <span className="truncate font-medium">{file?.name}</span>
        <span className="text-fg-muted text-footnote tabular-nums">
          {t("mem.csvRowsRead").replace("{rows}", String(sheet?.rows.length ?? 0))}
        </span>
      </span>
      <label className="text-callout text-accent cursor-pointer font-medium hover:underline">
        {t("mem.changeFile")}
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          className="sr-only"
          onChange={(event) => take(event.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  );

  /**
   * What the run came to, in a word, once.
   *
   * Green when something was written, red when nothing was: a toast is read at
   * a glance and its colour is most of what it says, so a green "0 members
   * created" is a run that failed wearing the face of one that worked.
   */
  const tell = (count: number) =>
    count > 0 ? toast.success(told(count)) : toast.error(told(count));

  /** The rows as they stand in the grid, posted as a file. */
  const send = async (rows: string[][], scope: "all" | "page") => {
    setError(null);
    setFinished(null);
    setProgress(null);
    setSkipped([]);
    // Held alongside the state, because the settle below runs in the same tick
    // the last frame arrives in and `applied`/`skipped` are a render behind.
    const gotApplied: AppliedRow[] = [];
    const gotSkipped: CsvResult["skipped"] = [];
    /**
     * Whether the server ever said anything about these rows.
     *
     * The settle below reads "sent, and not reported as failed" as "went
     * through" — which is true of an answer and a lie about a request that
     * never got one. A 500, a 503, a dropped connection: nothing was reported
     * failed because nothing was reported at all, and the file would move to
     * Success entire, having enrolled nobody.
     */
    let answered = false;
    const name = file?.name ?? "batch.csv";
    const csv = toCsv([sheet?.header ?? item.columns, ...rows]);
    const body = new FormData();
    // Built from the decoded text rather than forwarding the picked file: the
    // bytes that arrived may be Windows-1252, and what goes out is the UTF-8
    // the server reads.
    body.append("file", new File([csv], name, { type: "text/csv;charset=utf-8" }));
    setBusy(true);
    const controller = new AbortController();
    running.current = controller;
    try {
      if (!item.streams) {
        // Bulk enrolment still answers in one piece.
        const { data } = await api.post<CsvResult>(`/members/${item.path}`, body);
        answered = true;
        gotSkipped.push(...data.skipped);
        setSkipped(data.skipped);
        setFinished({
          counts: {
            total: (data.created ?? 0) + data.skipped.length,
            succeeded: data.created ?? 0,
            failed: data.skipped.length,
            unchanged: 0,
          },
          updated: data.created ?? 0,
          stopped: false,
        });
        landed.current = landed.current || (data.created ?? 0) > 0;
        // Nothing written is not a success, whatever the shape of the answer:
        // "0 members created" in green reads as "done" to anyone glancing at
        // it, and the rows are all still sitting there refused.
        tell(data.created ?? 0);
        return;
      }
      for await (const frame of postEventStream(
        `${api.defaults.baseURL}/members/${item.path}`,
        body,
        controller.signal,
      )) {
        // A frame is an answer: a run that died halfway still applied what it
        // applied, and `gotApplied` holds exactly that.
        answered = true;
        const payload: unknown = JSON.parse(frame.data);
        if (frame.event === "progress") {
          const chunk = payload as Progress & {
            applied: AppliedRow[];
            skipped: CsvResult["skipped"];
          };
          setProgress(chunk);
          if (chunk.applied.length) gotApplied.push(...chunk.applied);
          if (chunk.skipped.length) {
            gotSkipped.push(...chunk.skipped);
            setSkipped((held) => [...held, ...chunk.skipped]);
          }
          if (chunk.succeeded > 0) landed.current = true;
        } else if (frame.event === "done") {
          const last = payload as Finished;
          setFinished(last);
          tell(last.counts.succeeded);
        }
      }
    } catch (failure) {
      // An abort is the person pressing Stop; the run is already winding itself
      // up at the server's end, and its last frame may never arrive.
      if (controller.signal.aborted) setFinished(null);
      else if (failure instanceof StreamError)
        // A stream that never started answered with a problem+json, the same
        // shape every other error on this screen comes back as.
        setError((failure.problem as ProblemDetail | null)?.detail ?? t("mem.processFailed"));
      else setError(errorMessage(failure, t("mem.processFailed")));
    } finally {
      setBusy(false);
      running.current = null;
      // Nothing said, nothing settled: the rows stay exactly where they were,
      // still pending, with the error above them. The alternative is what this
      // used to do — read silence as success and empty the grid into the
      // Success tab after a request the server never answered.
      if (!answered) return;
      // What a run leaves behind: the rows still to deal with, back in the grid
      // they were sent from. The ones that went through are gone — they are
      // done, and sending them again only asks the server to decide all over
      // again that there is nothing to do — so what is left is exactly the work
      // left, and the buttons under it count that. A refused row carries its
      // own cells, which is what puts it back without the file.
      const header = sheet?.header ?? item.columns;
      const { succeeded, pending } = splitOutcome({
        header,
        sent: rows,
        applied: gotApplied,
        skipped: gotSkipped,
        streams: item.streams ?? false,
        sheetRows: sheet?.rows ?? [],
        partial: scope === "page",
      });
      setSheet({ header, rows: pending.map((row) => row.cells) });
      setRowErrors(pending.map((row) => (row.errors?.length ? row.errors : undefined)));
      setDone((held) => [...held, ...succeeded.map((row) => row.cells)]);
      setPage(1);
      // Every row in: the work is over, and the record of it is the answer.
      if (pending.length === 0) setTab("success");
    }
  };

  // A refusal the form can still act on — a file that would not parse, a
  // request the server turned down — belongs beside the field that caused it,
  // so it stays with the form rather than moving to the sending window.
  const errorNote = error ? (
    <p
      role="alert"
      className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{error}</span>
    </p>
  ) : null;

  const outcome = (
    <>
      {busy || progress ? (
        <ProgressNote progress={progress} busy={busy} onStop={() => running.current?.abort()} />
      ) : null}
      {errorNote}
    </>
  );

  // No banner of counts. The toast says how the run went, the block below
  // says what was wrong with each row, and a line repeating "0 created, 227
  // refused" between them is a third voice saying what two already said.

  /**
   * The refusals as legacy prints them: one line per distinct complaint, with
   * the rows that share it listed and cut off at five.
   *
   * Four hundred rows refused for the same reason is one sentence, not four
   * hundred — the old banner said "386 refused" and left the reading of them
   * to the grid, and the grid is where the *fixing* happens, not the
   * understanding. Sorted by the first row each group touches, so the list
   * reads in the order the file does.
   */
  const groupedErrors = useMemo(() => {
    const groups = new Map<string, { message: string; rows: number[] }>();
    for (const refusal of skipped) {
      // Every column the answer is about, not the first of them: a duplicate
      // identity is three columns, and naming one would read as a complaint
      // about that one.
      const named = refusal.fields?.length
        ? refusal.fields
        : refusal.field
          ? [refusal.field]
          : [];
      const columns = named.map((field) => labelFor(field, item.columns) ?? field).join(", ");
      const message = columns ? `${columns}: ${refusal.error}` : refusal.error;
      const group = groups.get(message) ?? { message, rows: [] };
      if (!group.rows.includes(refusal.row)) group.rows.push(refusal.row);
      groups.set(message, group);
    }
    return [...groups.values()]
      .map((group) => ({ ...group, rows: group.rows.sort((a, b) => a - b) }))
      .sort((a, b) => (a.rows[0] ?? 0) - (b.rows[0] ?? 0));
  }, [skipped, item.columns]);

  /** "1, 2, 3, 4, 5, and 386 more" — five, then a count. */
  const rowList = (rows: number[]) =>
    rows.length <= ERROR_ROWS_SHOWN
      ? rows.join(", ")
      : `${rows.slice(0, ERROR_ROWS_SHOWN).join(", ")}${t("mem.csvAndMore").replace(
          "{count}",
          String(rows.length - ERROR_ROWS_SHOWN),
        )}`;

  const refusals = groupedErrors.length ? (
    <div
      role="alert"
      className="bg-danger-soft text-danger flex items-start gap-3 rounded-lg px-3 py-2.5"
    >
      <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="text-callout grid gap-2">
        {groupedErrors.map((group) => (
          <p key={group.message}>
            <span className="font-semibold">
              {t("mem.csvRowList").replace("{rows}", rowList(group.rows))}
            </span>{" "}
            {group.message}
          </p>
        ))}
      </div>
    </div>
  ) : null;

  const reviewing = editing && sheet !== null && missing.length === 0;
  const { pages, shown } = pageSlice(sheet?.rows ?? [], page);

  const stopOrCancel = busy ? (
    <Button variant="secondary" onClick={() => running.current?.abort()}>
      {t("mem.csvStop")}
    </Button>
  ) : (
    <Button variant="secondary" onClick={close}>
      {t("common.cancel")}
    </Button>
  );

  // Sending is its own window, on the same reasoning as the export's. The form
  // above it has been answered and the editor behind it is a grid nobody should
  // be reading while rows are going in — and stacked under either one, a
  // receipt two thousand rows long pushed the buttons off the screen. Nothing
  // dismisses it while it is running: closing mid-file leaves a batch half
  // applied, which is the one outcome worth taking a click to avoid.
  // No screen of its own while it runs. The rows are what somebody watching a
  // send wants to see — which ones have gone, which are left — and swapping
  // them for a progress panel takes that away at the one moment it is worth
  // looking at. The grid stays, behind a spinner, with the progress above it.

  // Correcting a file is work, not a question, and it wants the room its own
  // columns need: a grid of a dozen of them inside a dialog the width of a form
  // is a grid nobody can read, and a two-column file stretched across the
  // screen is worse. It sizes to the file, up to the viewport. Still a
  // `<dialog>`, for Escape and the focus trap.
  if (reviewing && sheet) {
    return (
      <Modal
        open
        onClose={close}
        size="fit"
        title={`${title} · ${t(item.label)}`}
        description={t("mem.csvReady")
          .replace("{rows}", String(sheet.rows.length))
          .replace("{name}", file?.name ?? "")}
        footer={
          <>
            {stopOrCancel}
            {/* Both count the Pending tab, whichever tab is open: what has
                gone through is done, and a resend carries the remainder only.
                Two scopes, because a long file is corrected a page at a time,
                and sending the whole thing to try one page is how you find out
                the other ninety-nine were fine. */}
            {pages > 1 ? (
              <Button
                variant="secondary"
                disabled={busy || shown.length === 0}
                onClick={() => void send(shown, "page")}
              >
                {t("mem.submitThisPage").replace("{count}", String(shown.length))}
              </Button>
            ) : null}
            <Button
              disabled={busy || sheet.rows.length === 0}
              loading={busy}
              loadingLabel={t("mem.processing")}
              onClick={() => void send(sheet.rows, "all")}
            >
              {t("mem.submitAll").replace("{count}", String(sheet.rows.length))}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 pb-2">
          {outcome}
          {refusals}
          {/* The tabs appear once there is a record to split: before the first
              submit the whole file is pending and a tab strip over one tab is
              chrome with nothing to say. */}
          {done.length || finished ? (
            <Tabs
              label={t("mem.csvOutcome")}
              value={tab}
              onChange={setTab}
              items={[
                {
                  value: "pending" as const,
                  label: t("mem.csvPending"),
                  count: sheet.rows.length,
                },
                { value: "success" as const, label: t("mem.csvSucceeded"), count: done.length },
              ]}
            />
          ) : null}
          {tab === "success" && (done.length || finished) ? (
            done.length ? (
              <CsvEditor
                readOnly
                header={sheet.header}
                rows={done}
                page={donePage}
                onPageChange={setDonePage}
                busy={false}
                onChange={() => undefined}
              />
            ) : (
              <p className="text-fg-muted text-subhead py-6 text-center">
                {t("mem.csvNoneSucceeded")}
              </p>
            )
          ) : sheet.rows.length ? (
            <CsvEditor
              header={sheet.header}
              rows={sheet.rows}
              errors={rowErrors}
              types={COLUMN_TYPES}
              page={page}
              onPageChange={setPage}
              busy={busy}
              lead={fileRow}
              onChange={(rows) => setSheet({ header: sheet.header, rows })}
            />
          ) : (
            <p className="text-fg-muted text-subhead py-6 text-center">
              {t("mem.csvNonePending")}
            </p>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      size="lg"
      title={`${title} · ${t(item.label)}`}
      description={t("mem.csvDescription")
        .replace("{columns}", item.columns.join(", "))
        .replace("{hint}", t(item.hint).replace("{extra}", item.extra ? t(item.extra) : ""))}
      footer={<>{stopOrCancel}</>}
    >
      <div className="grid gap-4 pb-2">
        {/* The template first, as its own block with a rule under the heading:
            somebody opening this without a file needs the file before anything
            else on the screen is any use to them. */}
        <section className="grid gap-2">
          <p className="hairline-b text-fg text-callout flex items-center gap-2 pb-2.5 font-medium">
            <Download className="text-fg-subtle size-4" aria-hidden />
            {t("mem.templateHeading")}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="justify-self-start"
            onClick={template}
          >
            <CloudDownload aria-hidden /> {t("mem.downloadTemplate")}
          </Button>
          <p className="text-fg-muted text-footnote">{t("mem.templateHint")}</p>
        </section>

        <Field label={t("mem.csvFile")} htmlFor="csv-file">
          <Input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            required
            disabled={busy}
            className="file:text-fg py-2 file:mr-3 file:rounded-md file:border-0 file:bg-transparent file:font-medium"
            onChange={(event) => take(event.target.files?.[0] ?? null)}
          />
        </Field>

        {/* Checked here rather than by sending it: a missing column is a
            mistake about the file, and the answer to it is the template, not a
            round trip. */}
        {sheet && missing.length > 0 ? (
          <div
            role="alert"
            className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="grid gap-1.5">
              <span>{t("mem.csvMissingColumns").replace("{columns}", missing.join(", "))}</span>
              {/* Both headers spelled out: told only which column is missing,
                  somebody looking at a file whose header reads `Card No` has
                  no way to see what it should have read. */}
              <span className="text-footnote grid gap-0.5 opacity-80">
                <span>
                  {t("mem.csvExpectedHeader")}{" "}
                  <span className="font-mono">{item.columns.join(", ")}</span>
                </span>
                <span>
                  {t("mem.csvFileHeader")}{" "}
                  <span className="font-mono">{sheet.header.join(", ")}</span>
                </span>
              </span>
            </div>
          </div>
        ) : null}

        {errorNote}
      </div>
    </Modal>
  );
}

/**
 * How far the run has got, while it is getting there.
 *
 * A file of ten thousand rows is a minute of nothing to look at otherwise, and
 * a minute of nothing is indistinguishable from a minute of broken. Stop is
 * here rather than in the footer because it belongs to the thing it stops.
 */
function ProgressNote({
  progress,
  busy,
  onStop,
}: {
  progress: Progress | null;
  busy: boolean;
  onStop: () => void;
}) {
  const t = useTranslation();
  const done = progress?.done ?? 0;
  const total = progress?.total ?? 0;
  const share = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="border-line grid gap-2 rounded-xl border p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-fg text-callout tabular-nums">
          {total
            ? t("mem.csvProgress")
                .replace("{done}", String(done))
                .replace("{total}", String(total))
            : t("mem.processing")}
        </p>
        {busy ? (
          <Button variant="secondary" size="sm" onClick={onStop}>
            {t("mem.csvStop")}
          </Button>
        ) : null}
      </div>
      <div
        className="bg-surface-inset h-1.5 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={share}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="bg-accent h-full transition-[width] duration-300"
          style={{ width: `${share}%` }}
        />
      </div>
      {progress ? (
        <p className="text-fg-muted text-footnote tabular-nums">
          {t("mem.csvTally")
            .replace("{succeeded}", String(progress.succeeded))
            .replace("{failed}", String(progress.failed))}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advanced Filter: grouped "filter by" rows, plus the person's saved sets.
// ---------------------------------------------------------------------------
type FilterKind = "select" | "fk" | "text" | "date" | "boolean" | "daterange";

interface FilterField {
  name: string;
  label: string;
  type: FilterKind;
  options?: Opt[];
  loadOptions?: (signal?: AbortSignal) => Promise<{ value: string | number; label: string }[]>;
  /** `fk` only: the /compact endpoint; `dependsOn` names the field whose value narrows it. */
  endpoint?: string;
  dependsOn?: string;
}

/** The query keys a field writes: a range is two, everything else one. */
const keysOf = (field: FilterField) =>
  field.type === "daterange" ? [`${field.name}_from`, `${field.name}_to`] : [field.name];

/**
 * The filters the endpoint reads as a list, so the drawer picks several.
 *
 * Mirrors `_LIST_FILTERS` on the server, which is the same set legacy leaves
 * un-`singleSelect`. A yes/no filter is not here: two of three answers is the
 * same as no filter at all. Neither are `city_id` and `barangay_id` — the
 * barangay dropdown narrows to one city, and `/barangays/compact` takes one.
 */
const MANY = new Set([
  "membership_type",
  "membership_tier",
  "account_status",
  "account_type",
  "gender",
  "acquisition_type",
  "union_bank_tag",
  "card_replacement_reason",
  "signup_club_id",
  "active_club_id",
  "renew_club_id",
  "created_by_id",
  "updated_by_id",
]);

/** What a field starts as when it is picked: a list filter starts empty-listed. */
const blankFor = (field: FilterField): string | string[] => (MANY.has(field.name) ? [] : "");

const STATUS_PRESET = (t: Translate) =>
  opts(t, [
    ["all", "mem.presetAll"],
    ["active", "mem.presetActive"],
    ["active_primary", "mem.presetActivePrimary"],
    ["active_supplementary", "mem.presetActiveSupplementary"],
    ["cancelled", "mem.presetCancelled"],
    ["expired", "mem.presetExpired"],
    ["blacklisted", "mem.presetBlacklisted"],
    ["pending_to_print", "mem.presetPendingToPrint"],
  ]);

interface FilterGroup {
  title: string;
  /** The "Filter by" select is the value itself, not a choice of field. */
  direct?: FilterField;
  fields: FilterField[];
}

/**
 * The Advanced Filter's fields, in the shape `ResourcePage` reads chips with.
 *
 * `fk`, `select` and `boolean`: the three whose stored value is not what a
 * person should read — an id, an enum member, and `true`. A date already says
 * itself, and a `daterange` writes two keys under names of its own, which the
 * chips humanise perfectly well.
 *
 * `dependsOn` is deliberately not carried over. It says which field narrows the
 * *choices* when picking, and `ResourcePage` skips a dependent filter when
 * resolving because it cannot know the parent — but a chip is resolved by id
 * against ids it has already asked for, and an id needs no parent. Kept, and
 * Barangay would read as a number.
 */
const chipFilters = (t: Translate): FilterDef[] =>
  groupsOf(t)
    .flatMap((group) => [...(group.direct ? [group.direct] : []), ...group.fields])
    .filter(
      (field) => field.type === "fk" || field.type === "select" || field.type === "boolean",
    )
    .map((field) => ({
      name: field.name,
      label: field.label,
      type: field.type as "fk" | "select" | "boolean",
      ...(field.endpoint ? { endpoint: field.endpoint } : {}),
      ...(field.options ? { options: field.options } : {}),
    }));

const groupsOf = (t: Translate): FilterGroup[] => [
  {
    title: t("mem.groupMemberStatus"),
    direct: {
      name: "status",
      label: t("mem.groupMemberStatus"),
      type: "select",
      options: STATUS_PRESET(t),
    },
    fields: [],
  },
  {
    title: t("mem.groupCardInformation"),
    fields: [
      { name: "issued_date", label: t("mem.cardIssuedDate"), type: "daterange" },
      { name: "valid_until", label: t("mem.cardExpiryDate"), type: "daterange" },
      {
        // The reasons are configured, so the options are the list itself and
        // the value is the text — which is what the amendment row stores.
        name: "card_replacement_reason",
        label: t("mem.cardReplacementHistory"),
        type: "select",
        loadOptions: async (signal) => {
          const { data } = await api.get<{ items: { reason: string }[] }>("/reasons", {
            params: { kind: "card_replacement", size: 200, sort: "reason" },
            ...(signal ? { signal } : {}),
          });
          return data.items.map((r) => ({ value: r.reason, label: r.reason }));
        },
      },
      // Three, as legacy offers: a card's dates and why it was reissued. Print
      // readiness is asked through the Pending To Print preset above rather
      // than as a fourth condition here, and "never expires" is the answer to
      // the expiry date rather than a question of its own.
    ],
  },
  {
    title: t("mem.groupAccountDetails"),
    fields: [
      {
        name: "account_type",
        label: t("mem.accountType"),
        type: "select",
        options: ACCOUNT_TYPE(t),
      },
      {
        name: "membership_type",
        label: t("mem.membershipType"),
        type: "select",
        options: MEMBERSHIP_TYPE(t),
      },
      {
        name: "membership_tier",
        label: t("mem.membershipTier"),
        type: "select",
        options: TIER(t),
      },
    ],
  },
  {
    // Three clubs, as legacy has them. Where a member *lives* is the group
    // below: a club is a branch, and filing the two together sent people
    // looking for City under Club.
    title: t("mem.groupClubInformation"),
    fields: [
      {
        name: "signup_club_id",
        label: t("mem.signUpClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
      {
        name: "renew_club_id",
        label: t("mem.renewClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
      {
        name: "active_club_id",
        label: t("mem.activeClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
    ],
  },
  {
    title: t("mem.groupMembershipActivity"),
    fields: [
      {
        name: "volume_shopper",
        label: t("mem.volumeShopperTag"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withTag"],
          ["false", "mem.withoutTag"],
        ]),
      },
      {
        name: "union_bank_tag",
        label: t("mem.unionBankTag"),
        type: "select",
        options: CO_BRAND(t),
      },
    ],
  },
  {
    title: t("mem.groupMembershipProfile"),
    // Four, as legacy has them. "Email Address" here is the presence question —
    // who can be emailed at all — not a search for one; the search box above
    // the list is where a particular address is looked for.
    fields: [
      {
        name: "has_email",
        label: t("mem.emailAddress"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withEmail"],
          ["false", "mem.withoutEmail"],
        ]),
      },
      {
        name: "has_mobile",
        label: t("mem.mobileNumber"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withMobile"],
          ["false", "mem.withoutMobile"],
        ]),
      },
      { name: "dob", label: t("mem.birthdate"), type: "daterange" },
      { name: "gender", label: t("mem.gender"), type: "select", options: GENDER(t) },
    ],
  },
  {
    title: t("mem.groupPermission"),
    fields: [
      { name: "email_consent", label: t("mem.emailConsent"), type: "boolean" },
      { name: "phone_consent", label: t("mem.mobilePhoneConsent"), type: "boolean" },
    ],
  },
  {
    title: t("mem.groupSystemRelated"),
    fields: [
      { name: "created", label: t("mem.createdDate"), type: "daterange" },
      { name: "updated", label: t("mem.lastModifiedDate"), type: "daterange" },
      {
        name: "updated_by_id",
        label: t("mem.modifiedBy"),
        type: "fk",
        endpoint: "/users/compact",
      },
      {
        name: "acquisition_type",
        label: t("mem.sourceOfApplication"),
        type: "select",
        options: ACQUISITION(t),
      },
      {
        name: "created_by_id",
        label: t("mem.createdByFilter"),
        type: "fk",
        endpoint: "/users/compact",
      },
    ],
  },
  {
    // Last, because it is the one section legacy has no heading for: it asks
    // through the Member Status cascade's District option, which is a club
    // type listing locations, and the flat form has no cascade to hang that
    // off. Everything above is legacy's own order, heading for heading; these
    // two stand on their own at the end rather than displace one of them.
    title: t("mem.groupAddress"),
    fields: [
      { name: "city_id", label: t("mem.city"), type: "fk", endpoint: "/cities/compact" },
      {
        name: "barangay_id",
        label: t("mem.barangay"),
        type: "fk",
        endpoint: "/barangays/compact",
        dependsOn: "city_id",
      },
    ],
  },
];

interface SavedFilter {
  id: number;
  name: string;
  filters: FilterValues;
  owner: string | null;
  mine: boolean;
  created_at: string;
}

const SAVED_KEY = ["/members/saved-filters"];

/**
 * Whether a saved condition and the one on screen say the same thing.
 *
 * Not `===`: a list filter holds an array, and two arrays are never equal
 * however alike. Both sides are written sorted — `OptionSelect` sorts on every
 * pick — so joining is enough, and the comparison stays one line rather than a
 * deep-equality helper this file would need nowhere else.
 */
const sameValue = (mine: string | string[] | undefined, theirs: string | string[]): boolean =>
  (Array.isArray(mine) ? mine.join("\u0000") : (mine ?? "")) ===
  (Array.isArray(theirs) ? theirs.join("\u0000") : String(theirs));
/**
 * Whether a saved set is what the list is currently showing: every condition
 * it holds is on screen, and nothing else is. The button that applied it says
 * so, and Save offers to write back to it rather than make a copy.
 */
const sameFilters = (saved: FilterValues, shown: FilterValues): boolean =>
  Object.entries(saved).every(([key, value]) => sameValue(shown[key], value)) &&
  Object.entries(shown).every(([key, value]) => !isSet(value) || key in saved);
// `queryOptions` rather than a bare object: it is what types the query context,
// so `signal` is an AbortSignal here rather than an implicit any.
const savedFiltersQuery = queryOptions({
  queryKey: SAVED_KEY,
  queryFn: async ({ signal }) =>
    (await api.get<SavedFilter[]>("/members/saved-filters", { signal })).data,
});

/** What a saved set says about itself: how many conditions, and when. */
function savedSummary(row: SavedFilter, t: (key: TranslationKey) => string): string {
  const count = Object.values(row.filters).filter(isSet).length;
  return `${t("mem.filtersCount").replace("{count}", String(count))} · ${day(row.created_at)}`;
}

/**
 * The saved sets beside the filter button, so applying one is a click rather
 * than a trip through the drawer. The button names whichever set the list is
 * currently showing, and picking that same one again takes it off — the way
 * out of a saved filter is the control that put it on.
 *
 * Nothing at all until there is something to pick: an empty menu beside the
 * funnel is a button that only ever disappoints. Renaming, deleting and saving
 * stay in the drawer, where the conditions they are about are.
 */
function SavedFiltersDropdown({
  values,
  onApply,
}: {
  values: FilterValues;
  onApply: (values: FilterValues) => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { id, button, panelProps, close } = useAnchoredPopover(260);
  const allow = useCan();
  // Each verb has its own codename now, and the routes ask for them, so a
  // control left on screen is one whose request comes back 403.
  const mayRead = allow("view__member_saved_filter");
  const mayRename = allow("change__member_saved_filter");
  const mayDelete = allow("delete__member_saved_filter");
  const saved = useQuery({ ...savedFiltersQuery, enabled: mayRead });
  /** Renaming one, which is this menu's job: the drawer saves, this keeps. */
  const [renaming, setRenaming] = useState<SavedFilter | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SAVED_KEY });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch(`/members/saved-filters/${id}`, { name }),
    onSuccess: () => {
      setRenaming(null);
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("mem.renameFailed"))),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/members/saved-filters/${id}`),
    onSuccess: () => {
      toast.success(t("mem.filterDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("mem.filterDeleteFailed"))),
  });
  const rows = saved.data ?? [];
  const applied = (row: SavedFilter) => sameFilters(row.filters, values);
  const current = rows.find(applied);
  if (rows.length === 0) return null;

  return (
    <>
      <Button
        ref={button}
        variant="secondary"
        popoverTarget={id}
        className={cn(current && "border-accent text-accent")}
      >
        {current?.name ?? t("mem.savedFilters")} <ChevronDown aria-hidden />
      </Button>
      <div {...panelProps}>
        {rows.map((row) => (
          <div
            key={row.id}
            className="hover:bg-surface-inset flex items-center gap-1 rounded-lg"
          >
            <button
              type="button"
              onClick={() => {
                close();
                // Picking the set that is already on takes it off: the way out
                // of a saved filter is the control that put it on.
                onApply(applied(row) ? {} : row.filters);
              }}
              className="min-w-0 flex-1 rounded-lg px-2.5 py-2 text-left"
            >
              <span
                className={cn(
                  "text-callout block truncate font-medium",
                  applied(row) && "text-accent",
                )}
              >
                {row.name}
              </span>
              <span className="text-fg-muted text-footnote block tabular-nums">
                {applied(row) ? t("mem.applied") : savedSummary(row, t)}
              </span>
            </button>
            {/* Only your own: the API refuses the rest, and a superuser sees
                everybody's here. */}
            {row.mine ? (
              <span className="flex shrink-0 items-center gap-0.5 pr-1">
                {mayRename ? (
                  <button
                    type="button"
                    title={t("common.edit")}
                    onClick={() => {
                      close();
                      setRenaming(row);
                    }}
                    className="text-fg-subtle hover:bg-surface-raised hover:text-fg grid size-7 place-items-center rounded-lg transition-colors"
                  >
                    <Pencil className="size-3.5" aria-hidden />
                    <span className="sr-only">
                      {t("mem.editName").replace("{name}", row.name)}
                    </span>
                  </button>
                ) : null}
                {mayDelete ? (
                  <button
                    type="button"
                    title={t("common.delete")}
                    onClick={() => remove.mutate(row.id)}
                    className="text-fg-subtle hover:bg-danger-soft hover:text-danger grid size-7 place-items-center rounded-lg transition-colors"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    <span className="sr-only">
                      {t("mem.deleteName").replace("{name}", row.name)}
                    </span>
                  </button>
                ) : null}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <FilterNameModal
        naming={renaming ? { id: renaming.id, name: renaming.name } : null}
        title={t("mem.renameFilter")}
        loading={rename.isPending}
        onClose={() => setRenaming(null)}
        onSubmit={(name) => renaming && rename.mutate({ id: renaming.id, name })}
      />
    </>
  );
}

/**
 * One field, so it is a modal rather than a browser prompt — the same shape
 * whether the set is new, being written back to, or being renamed. What it is
 * doing is the caller's to say: the three are one typed name apart, and the
 * modal cannot tell them apart from `naming` alone.
 */
function FilterNameModal({
  naming,
  title,
  description,
  loading,
  onClose,
  onSubmit,
}: {
  naming: { id?: number; name: string } | null;
  title: string;
  description?: string | undefined;
  loading: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const t = useTranslation();
  const [name, setName] = useState("");
  // The field starts with the current name each time the modal opens.
  const [openedFor, setOpenedFor] = useState<typeof naming>(null);
  if (naming !== openedFor) {
    setOpenedFor(naming);
    setName(naming?.name ?? "");
  }
  return (
    <Modal
      open={naming !== null}
      onClose={onClose}
      size="sm"
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="filter-name" loading={loading}>
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <Form
        id="filter-name"
        className="pb-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          onSubmit(trimmed);
          onClose();
        }}
      >
        <Field label={t("mem.name")} htmlFor="filter-name-input">
          <Input
            id="filter-name-input"
            autoFocus
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      </Form>
    </Modal>
  );
}

/**
 * The Advanced Filter, against the right edge of the screen.
 *
 * A drawer rather than a centred sheet, which is what legacy has and what the
 * conditions themselves want: one narrow column of short rows read top to
 * bottom, beside the list it narrows rather than on top of it.
 *
 * The header carries Save at one edge and the close at the other, with the
 * name of whatever set is on in the middle. The footer is the two verbs that
 * end the visit — clear everything, or filter by what is set.
 */
function AdvancedFilter({
  values,
  onApply,
  onClose,
}: {
  values: FilterValues;
  onApply: (values: FilterValues) => void;
  onClose: () => void;
}) {
  const t = useTranslation();
  const GROUPS = useMemo(() => groupsOf(t), [t]);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FilterValues>(values);
  /** Extra "Select field" rows per group, beyond the one every group shows when empty. */
  const [blanks, setBlanks] = useState<Record<string, number>>({});
  /**
   * The Member Status rows: a preset and a club condition each, held here
   * rather than read back out of the draft. Two rows asking two different
   * things is the whole point of adding one, and the draft has one `status`
   * key to answer with — so a row that read its value from there would come up
   * already holding whatever the row above it holds.
   */
  const [statusRows, setStatusRows] = useState<StatusRow[]>(() => statusRowsOf(values));
  /** Naming the set, while Save is open. */
  const [naming, setNaming] = useState<{ id?: number; name: string } | null>(null);
  const allowSaving = useCan();
  // Keeping a new one and writing back to an existing one are two grants, and
  // the routes ask for them separately. The button is offered for whichever of
  // the two this press would be.
  const saved = useQuery(savedFiltersQuery);
  /**
   * The saved set the list is showing, if it is showing one.
   *
   * Save offers its name, and saving under that same name writes back to it
   * rather than leaving two sets that say the same thing. Changing the name
   * saves a new one — legacy's rule, and the modal says so while it is being
   * typed, because the difference is one letter and not a button.
   */
  const current = (saved.data ?? []).find((row) => sameFilters(row.filters, values));

  const set = (name: string, value: string | string[]) =>
    setDraft((d) => ({ ...d, [name]: value }));
  const clearKeys = (keys: string[]) =>
    setDraft((d) => {
      const next = { ...d };
      for (const key of keys) delete next[key];
      return next;
    });
  const addBlank = (title: string, delta: number) =>
    setBlanks((b) => ({ ...b, [title]: Math.max(0, (b[title] ?? 0) + delta) }));

  const save = useMutation({
    mutationFn: async ({ id, name }: { id?: number; name: string }) => {
      const body = {
        name,
        filters: Object.fromEntries(Object.entries(draft).filter(([, v]) => isSet(v))),
      };
      return id
        ? api.patch(`/members/saved-filters/${id}`, body)
        : api.post("/members/saved-filters", body);
    },
    onSuccess: () => {
      toast.success(t("mem.filterSaved"));
      void queryClient.invalidateQueries({ queryKey: SAVED_KEY });
    },
    onError: (error) => toast.error(errorMessage(error, t("mem.filterSaveFailed"))),
  });

  const active = (group: FilterGroup) =>
    group.fields.filter((f) => keysOf(f).some((k) => k in draft));
  /** Pick a field for a row: "" is All, which drops the group's filters. */
  const choose = (group: FilterGroup, from: FilterField | null, name: string) => {
    if (from) clearKeys(keysOf(from));
    else addBlank(group.title, -1);
    if (!name) {
      clearKeys(group.fields.flatMap(keysOf));
      return;
    }
    const field = group.fields.find((f) => f.name === name);
    if (field) for (const key of keysOf(field)) set(key, blankFor(field));
  };

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        // 400px, which is what legacy opens this one at — its `small` preset,
        // passed as `width={400}`. The shared scale's nearest step is 448 and
        // the difference shows: the conditions are one column of short rows,
        // and the extra inch is all trailing white.
        width="md"
        className="sm:max-w-[25rem]"
        // The sheet's own name, capitalised as a title is; the button that
        // opens it is a sentence-case verb phrase. Legacy writes both.
        title={current?.name ?? t("mem.advancedFilterTitle")}
        lead={
          allowSaving(current ? "change__member_saved_filter" : "add__member_saved_filter") ? (
            <Button
              variant="secondary"
              size="sm"
              title={current ? t("mem.updateFilter") : t("mem.saveThisFilter")}
              onClick={() =>
                setNaming({ ...(current ? { id: current.id } : {}), name: current?.name ?? "" })
              }
            >
              {t("rp.save")}
            </Button>
          ) : null
        }
        footer={
          // One row, both verbs, and Filter twice the width of Reset: the one
          // that acts on what was typed is the one the thumb lands on.
          <div className="flex w-full gap-2.5">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setDraft({});
                setBlanks({});
                setStatusRows([BLANK_STATUS_ROW]);
              }}
            >
              {t("mem.reset")}
            </Button>
            <Button className="flex-[2]" onClick={() => onApply(draft)}>
              {t("mem.filterAction")}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Above the sections, as legacy has it: the one condition asked of
              nearly every list, before the ones asked of some of them. It
              writes the same two keys System Related's Created Date does —
              the day a membership was written down is the day it began. */}
          <section>
            <p className="text-callout text-heading font-semibold">
              {t("mem.membershipSince")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="grid gap-1">
                <span className="text-fg-muted text-subhead">{t("mem.from")}</span>
                <DatePicker
                  id="af-since-from"
                  placeholder={t("mem.fromDate")}
                  value={String(draft.created_from ?? "")}
                  onChange={(next) => set("created_from", next)}
                />
              </div>
              <div className="grid gap-1">
                <span className="text-fg-muted text-subhead">{t("mem.to")}</span>
                <DatePicker
                  id="af-since-to"
                  placeholder={t("mem.toDate")}
                  value={String(draft.created_to ?? "")}
                  onChange={(next) => set("created_to", next)}
                />
              </div>
            </div>
          </section>

          {GROUPS.map((group) => {
            const rows = active(group);
            const blankCount = group.direct
              ? 0
              : Math.max(blanks[group.title] ?? 0, rows.length === 0 ? 1 : 0);
            return (
              <section key={group.title}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-callout text-heading font-semibold">{group.title}</p>
                  {/* Legacy's own: a ghost button at the section's type size,
                      in body ink rather than the muted grey a ghost takes by
                      default, and a typed "+" rather than an icon — beside a
                      13px heading a drawn glyph reads as a different weight. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-callout text-fg px-2.5"
                    onClick={() =>
                      group.direct
                        ? // Empty, always: an added row is a new question, not
                          // a copy of the one above it.
                          setStatusRows((rows) => [...rows, BLANK_STATUS_ROW])
                        : addBlank(group.title, 1)
                    }
                  >
                    + {t("mem.addRow")}
                  </Button>
                </div>

                {group.direct ? (
                  <MemberStatusRows
                    field={group.direct}
                    draft={draft}
                    set={set}
                    clearKeys={clearKeys}
                    rows={statusRows}
                    onRows={setStatusRows}
                  />
                ) : null}

                {rows.map((field) => (
                  <FilterRow key={field.name} onClear={() => clearKeys(keysOf(field))}>
                    <OptionSelect
                      value={field.name}
                      onChange={(next) => choose(group, field, next)}
                      choices={group.fields
                        .filter((f) => f === field || !rows.includes(f))
                        .map((f) => ({ value: f.name, label: f.label }))}
                      placeholder={t("mem.all")}
                    />
                    <FilterValue field={field} draft={draft} onChange={set} />
                  </FilterRow>
                ))}

                {Array.from({ length: blankCount }, (_, i) => (
                  <FilterRow key={`blank-${i}`} onClear={() => addBlank(group.title, -1)}>
                    <OptionSelect
                      value=""
                      onChange={(next) => choose(group, null, next)}
                      choices={group.fields
                        .filter((f) => !rows.includes(f))
                        .map((f) => ({ value: f.name, label: f.label }))}
                      placeholder={t("mem.selectField")}
                    />
                  </FilterRow>
                ))}
              </section>
            );
          })}
        </div>
      </Drawer>

      <FilterNameModal
        naming={naming}
        title={naming?.id ? t("mem.updateFilter") : t("mem.saveThisFilter")}
        description={naming?.id ? t("mem.updateFilterHint") : t("mem.saveHint")}
        loading={save.isPending}
        onClose={() => setNaming(null)}
        onSubmit={(name) =>
          save.mutate(naming?.id && name === naming.name ? { id: naming.id, name } : { name })
        }
      />
    </>
  );
}

/** Which club a Member Status preset is read against, and which club that is. */
const CLUB_KEYS = ["renew_club_id", "signup_club_id", "active_club_id"] as const;

/** One Member Status condition: a preset, and the club it is read against. */
interface StatusRow {
  preset: string;
  /** "" until asked, then "all" or one of `CLUB_KEYS`. */
  kind: string;
}

const BLANK_STATUS_ROW: StatusRow = { preset: "", kind: "" };

/**
 * The rows a set of applied filters came from.
 *
 * One row per club condition, because that is what a row can hold that another
 * cannot; the preset goes on the first, since the list has only ever applied
 * one. Reopening the drawer on a filter someone saved therefore shows the rows
 * that would have produced it.
 */
const statusRowsOf = (values: FilterValues): StatusRow[] => {
  const preset = typeof values.status === "string" ? values.status : "";
  const asked = CLUB_KEYS.filter((key) => isSet(values[key]));
  if (asked.length === 0) return [{ preset, kind: "" }];
  return asked.map((kind, index) => ({ preset: index === 0 ? preset : "", kind }));
};

/**
 * Member Status: a preset, the club it is read against, and as many of those
 * as somebody wants.
 *
 * Every row is its own question — a new one starts empty, and taking one away
 * takes its answer with it, so the next row added starts empty too. What a row
 * holds lives in `rows` and not in the draft: the draft has one `status` key,
 * and rows reading their value from it would each come up holding the one the
 * row above had chosen.
 *
 * The list still applies one status, because the API takes one. The last row
 * that names a preset is the one it reads — legacy resolves the same conflict
 * the same way — and a preset another row is already asking about is not
 * offered twice. What rows genuinely add is club conditions: Renew Club at
 * Angeles *and* Sign-up Club somewhere else is two rows, because it is two
 * keys.
 */
function MemberStatusRows({
  field,
  draft,
  set,
  clearKeys,
  rows,
  onRows,
}: {
  field: FilterField;
  draft: FilterValues;
  set: (name: string, value: string | string[]) => void;
  clearKeys: (keys: string[]) => void;
  rows: StatusRow[];
  onRows: (rows: StatusRow[]) => void;
}) {
  const t = useTranslation();
  /** Hold the rows, and put the status they add up to on the draft. */
  const apply = (next: StatusRow[]) => {
    onRows(next);
    const named = [...next].reverse().find((row) => row.preset !== "");
    if (named) set(field.name, named.preset);
    else clearKeys([field.name]);
  };
  /** A club condition is a filter key; dropping the row drops the filter. */
  const forget = (row: StatusRow) => {
    if (row.kind && row.kind !== "all") clearKeys([row.kind]);
  };

  return (
    <>
      {rows.map((row, index) => (
        <FilterRow
          key={index}
          onClear={() => {
            forget(row);
            // The first row is the section itself and stays, emptied. The rest
            // go, and go completely: nothing of a removed row is left behind
            // for the next one added to inherit.
            apply(
              rows.length === 1
                ? [BLANK_STATUS_ROW]
                : rows.filter((_, other) => other !== index),
            );
          }}
        >
          <OptionSelect
            value={row.preset}
            onChange={(next) => {
              // A different question wants a fresh answer under it.
              forget(row);
              apply(
                rows.map((held, other) =>
                  other === index ? { preset: next, kind: "" } : held,
                ),
              );
            }}
            choices={(field.options ?? []).filter(
              (option) =>
                option.value === row.preset ||
                !rows.some((held, other) => other !== index && held.preset === option.value),
            )}
            placeholder={t("mem.selectField")}
          />
          {/* Legacy's cascade: a preset is a count of members, and the next
              question is always which club it is counted at. It opens only
              once there is a preset to read against. */}
          {row.preset ? (
            <ClubTypeCascade
              draft={draft}
              set={set}
              kind={row.kind}
              onKind={(next) => {
                forget(row);
                onRows(
                  rows.map((held, other) => (other === index ? { ...held, kind: next } : held)),
                );
              }}
              taken={rows.filter((_, other) => other !== index).map((held) => held.kind)}
            />
          ) : null}
        </FilterRow>
      ))}
    </>
  );
}

/**
 * The second and third questions under a Member Status preset, as legacy asks
 * them: which club the count is read against, and then which club.
 *
 * "All" is every club, and legacy uses it to ask *when* instead — the same two
 * dates Membership Since at the top of the drawer writes, because a count of
 * members with no club named is a count over a stretch of time. Both controls
 * show the same value, which is the honest way to draw one filter twice.
 *
 * The three club filters are the ones Club Information sets, so a club picked
 * here is the club shown there. Nothing new goes to the API: this is a second
 * way into the same three keys, which is exactly what legacy does with them.
 */
function ClubTypeCascade({
  draft,
  set,
  kind,
  onKind,
  taken,
}: {
  draft: FilterValues;
  set: (name: string, value: string | string[]) => void;
  kind: string;
  /** The row owns what a kind leaves behind, so changing one goes through it. */
  onKind: (next: string) => void;
  /** Kinds another row is already asking about, which this one cannot repeat. */
  taken: string[];
}) {
  const t = useTranslation();
  const choices = [
    { value: "all", label: t("mem.all") },
    { value: "renew_club_id", label: t("mem.renewClub") },
    { value: "signup_club_id", label: t("mem.signUpClub") },
    { value: "active_club_id", label: t("mem.activeClub") },
  ].filter((choice) => choice.value === kind || !taken.includes(choice.value));
  return (
    <>
      <ValueField label={t("mem.clubType")}>
        <OptionSelect
          value={kind}
          onChange={onKind}
          choices={choices}
          placeholder={t("mem.selectClubType")}
        />
      </ValueField>

      {kind === "all" ? (
        <div className="grid grid-cols-2 gap-3">
          <ValueField label={t("mem.fromDate")}>
            <DatePicker
              placeholder={t("mem.selectDate")}
              value={String(draft.created_from ?? "")}
              onChange={(next) => set("created_from", next)}
            />
          </ValueField>
          <ValueField label={t("mem.toDate")}>
            <DatePicker
              placeholder={t("mem.selectDate")}
              value={String(draft.created_to ?? "")}
              onChange={(next) => set("created_to", next)}
            />
          </ValueField>
        </div>
      ) : null}

      {kind && kind !== "all" ? (
        <ValueField label={t("mem.club")}>
          <FkSelect
            endpoint="/clubs/compact"
            value={draft[kind] ?? []}
            onChange={(next) => set(kind, next)}
            placeholder={t("rp.selectDots")}
          />
        </ValueField>
      ) : null}
    </>
  );
}

/** "Filter by" with its clear button, then whatever the row holds. */
function FilterRow({ onClear, children }: { onClear: () => void; children: ReactNode }) {
  const t = useTranslation();
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        <span className="text-fg-muted text-subhead">{t("mem.filterBy")}</span>
        <ClearButton focusable label={t("mem.removeFilter")} onClear={onClear} />
      </div>
      <div className="mt-1 grid gap-2">{children}</div>
    </div>
  );
}

/** The value control for a chosen field: data according to its kind. */
function FilterValue({
  field,
  draft,
  onChange,
}: {
  field: FilterField;
  draft: FilterValues;
  onChange: (name: string, value: string | string[]) => void;
}): ReactNode {
  const t = useTranslation();
  const remote = useQuery({
    queryKey: ["options", "filter", field.name],
    queryFn: ({ signal }) => field.loadOptions?.(signal) ?? Promise.resolve([]),
    enabled: Boolean(field.loadOptions),
    staleTime: 300_000,
  });
  if (field.type === "daterange") {
    const from = `${field.name}_from`;
    const to = `${field.name}_to`;
    return (
      <div className="grid grid-cols-2 gap-3">
        <ValueField label={t("mem.fromDate")}>
          <DatePicker
            placeholder={t("mem.selectDate")}
            value={String(draft[from] ?? "")}
            onChange={(next) => onChange(from, next)}
          />
        </ValueField>
        <ValueField label={t("mem.toDate")}>
          <DatePicker
            placeholder={t("mem.selectDate")}
            value={String(draft[to] ?? "")}
            onChange={(next) => onChange(to, next)}
          />
        </ValueField>
      </div>
    );
  }
  if (field.type === "fk") {
    // A dependent list narrows to one parent, so the parent is read as one
    // value — which is why `city_id` stays a single choice.
    const chosen = field.dependsOn ? draft[field.dependsOn] : undefined;
    const parent = Array.isArray(chosen) ? (chosen[0] ?? "") : (chosen ?? "");
    const waiting = Boolean(field.dependsOn) && parent === "";
    return (
      <ValueField label={field.label}>
        <FkSelect
          endpoint={field.endpoint ?? ""}
          value={draft[field.name] ?? blankFor(field)}
          onChange={(next) => onChange(field.name, next)}
          params={field.dependsOn ? { [field.dependsOn]: parent } : undefined}
          disabled={waiting}
          placeholder={
            waiting
              ? t("rp.chooseFirst").replace(
                  "{parent}",
                  field.dependsOn?.replace(/_id$/, "") ?? "",
                )
              : t("rp.selectDots")
          }
        />
      </ValueField>
    );
  }
  if (field.type === "select" || field.type === "boolean") {
    const options: Opt[] =
      field.type === "boolean"
        ? [
            { value: "true", label: t("rp.yes") },
            { value: "false", label: t("rp.no") },
          ]
        : (field.options ??
          (remote.data ?? []).map((o) => ({ value: String(o.value), label: o.label })));
    return (
      <ValueField label={field.label}>
        <OptionSelect
          value={draft[field.name] ?? blankFor(field)}
          onChange={(next) => onChange(field.name, next)}
          choices={options}
          placeholder={t("rp.selectDots")}
        />
      </ValueField>
    );
  }
  const typed = draft[field.name];
  return (
    <ValueField label={field.label}>
      <Input
        type={field.type}
        aria-label={field.label}
        value={typeof typed === "string" ? typed : ""}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
    </ValueField>
  );
}

/**
 * A value control under the caption that says what it is asking.
 *
 * The "Filter by" select above it names the field, and then the control under
 * it is a bare box: which of the two a value belongs to is guesswork on a row
 * holding three of them, and a date range is two boxes that look alike.
 */
function ValueField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1">
      <span className="text-fg-muted text-subhead">{label}</span>
      {children}
    </div>
  );
}

export default MembersPage;
